import { GlobalConfigType, setGlobalConfig } from 'actions'
import cloneDeep from 'clone-deep'
import { OptionModal } from 'components/Modals/OptionModal'
import { OtherPaymentLine, OtherPaymentsEditor, sumOtherPayments } from 'components/OtherPayments'
import { Payee } from 'components/PayeeCombo'
import { PayoffChargeLine, PayoffChargesEditor, sumPayoffCharges } from 'components/PayoffCharges'
import { InputType, SettingKey } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  getLoanChargeCategories,
  getLoanPayees,
  getLoanPaymentFillData,
  getLoanPaymentLateChargeCalculation,
  getPayoffPaymentCharges,
  getPhonePaymentAvailability,
} from 'services/apis/loan/servicing'
import { Button, Checkbox, Modal } from 'stories/components'
import {
  fillObject,
  formatDate,
  getNextPaymentDate,
  InputValidate,
  openAuditLog,
  prompt,
  removeComma,
  roundToCents,
  thousandSeperator,
  thousandSeperatorWithPrefix,
} from 'utils'
import { getOptionValues, IOptionType } from 'utils/general'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs, LoanPaymentType } from './constants'
import { DistributionRow, DistributionsModal } from './DistributionsModal'
import { IPayCharges, LoanChargesModal } from './LoanChargesModal'
import { adjustFieldKeys, DeferOption, LoanPaymentAdjustModal } from './LoanPaymentAdjustModal'
import { allocatableInterest, payoffInterestDays, payoffPerDiem } from './payoffInterest'
import { amountPaidFollowsOwed, waivedInterest } from './payoffOwed'
import { BORROWER_OVERAGE_MEMO, otherPaymentsCollected, suspenseAfterRefund } from './payoffRefund'
import { PayoffSuspenseSummary } from './PayoffSuspenseSummary'
import { PhonePaymentModal } from './PhonePaymentModal'

// Cash entries outside the amortization schedule: no interest/principal auto-split.
const OTHER_CASH_TYPES = ['otherCashFromBorrower', 'otherCashFromLender']

/**
 * Buckets a payment can draw on besides the borrower's cash, each a `source*` field on the form
 * and a column on the payment.
 *
 * Listed once so every total that has to count them counts the same set: the interest/principal
 * allocator, its breakdown, the save and phone-payment guards, and the adjust dialog. Each was
 * written out by hand and they drifted apart: the dialog was missing two of them, so it reported a
 * shortfall over money the payment already had, and offered to defer or waive it.
 */
const SOURCE_OF_FUNDS_KEYS = [
  'sourceSuspense',
  'sourceImpound',
  'sourceInterestReserve',
  'sourceConstructionReserve',
  'sourceEscrowRepairHoldback',
  'sourceTrustBalance',
]

export const LoanPaymentsModal = ({
  editData,
  payments,
  paymentInfo,
  onClose,
  onOk,
  loading,
  loanNumber: loanNumberProp,
  prefill,
  onPhonePaymentApplied,
}: {
  editData: Record<string, any>
  payments: Array<Record<string, any>>
  paymentInfo: Record<string, any>
  onClose: () => void
  onOk: Function
  loading: boolean
  loanNumber?: string | number
  // Optional initial state for a NEW payment: force a payment type and/or amount paid.
  prefill?: { type?: string; amountPaid?: number }
  // Called after a payment was charged over the phone and posted by the backend. The payment
  // already exists at this point, so the caller just refreshes and closes.
  onPhonePaymentApplied?: (result: Record<string, any>) => void
}) => {
  const dispatch = useDispatch()

  const { hasPermission } = usePermissions()

  const paymentCodeOptions = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.PAYMENT_CODE] || [])
  const paymentMethodOptions = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.PAYMENT_METHOD] || [],
  )
  // Where funds above what the loan owes are credited (Admin > Rules Configuration > Servicing).
  // Mirrors the payment import's `overpayment` option, which has always defaulted to suspense.
  // 'principal' restores the pre-fix behaviour of capitalizing the surplus.
  const overpaymentAppliesTo = useSelector((state: any) =>
    state.globalConfigReducer[GlobalConfigType.RulesConfig]?.paymentOverpaymentAppliesTo === 'principal'
      ? 'principal'
      : 'suspense',
  )

  // Use an explicit loan number when embedded outside a loan route (e.g. the pipeline grid overlay).
  const loanNumber = loanNumberProp && Number(loanNumberProp) > 0 ? Number(loanNumberProp) : setLoanNumber()
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs(loanNumber))
  const [showLoanCharges, setShowLoanCharges] = useState(false)
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [paymentModal, setPaymentModal] = useState<SettingKey | null>(null)
  const [pendingData, setPendingData] = useState<Record<string, any> | null>(null)
  const [showInterestCalc, setShowInterestCalc] = useState(false)
  const [showDistributions, setShowDistributions] = useState(false)
  // Interest this payment leaves uncollected is carried as an Unpaid Interest receivable unless the
  // servicer says they are forgiving it. Null is the default (carry it); a string (possibly empty)
  // is the waiver and its reason - the shortfall is still booked, then waived on the spot, so the
  // loan keeps the record that the interest was charged and then given up.
  const [waiveUnpaidInterest, setWaiveUnpaidInterest] = useState<string | null>(null)
  // The same waiver, readable synchronously: turning it on has to re-split the payment in the same
  // tick, and the state the split would close over is still the old one at that point.
  const waiveUnpaidInterestRef = useRef<string | null>(null)
  // How much waived interest is currently OFF Amount Due. Waiving it takes it off what the payoff
  // has to collect; keeping the figure means a later edit to Interest moves Amount Due by the
  // change in the waiver rather than by the whole of it a second time.
  const waivedInterestOff = useRef(0)
  // "Take Payment by Phone": offered only for a new payment, only to a user who holds the
  // Loan.Servicing.PhonePayment permission, and only where the tenant's payment gateway accepts
  // keyed-in card / ACH details.
  const canTakePhonePayment = hasPermission('Loan.Servicing.PhonePayment') === true
  // Three states, not two. 'checking' hides the action while we ask; `true` / `false` are the
  // tenant's real answer; 'unknown' means the question could not be put at all. A failed request
  // used to be recorded as `false`, so one dropped GET - a task being replaced mid-rollout is
  // enough - hid the action for the rest of the modal's life with nothing on screen to say why,
  // and the only way back was to close and reopen the screen. Offering it on 'unknown' is safe:
  // the charge re-checks the provider server side and refuses with a message staff can read.
  const [phonePaymentAvailable, setPhonePaymentAvailable] = useState<boolean | 'checking' | 'unknown'>('checking')
  const [phonePaymentData, setPhonePaymentData] = useState<Record<string, any> | null>(null)
  // Edited participant split. Null keeps the automatic ownership split; a non-null array (set once
  // the user confirms the Participant Distributions modal) is sent with the payment as an override.
  const [distributions, setDistributions] = useState<DistributionRow[] | null>(null)
  // Default-rate details captured when a payment is filled, so the Interest
  // Calculation popup can show how Default Interest was derived.
  const [defaultRateInfo, setDefaultRateInfo] = useState<{ rate: number; days: number }>({ rate: 0, days: 0 })
  /**
   * The default-rate premium the penalty calc assessed for this payment, which is what Default
   * Interest is auto-filled with. A ref and not state because `applyPenalty` and the re-split that
   * follows it run in the same tick, so a setState here would leave the split reading the previous
   * payment's figure. 0 means "not known on this screen" - an edit of an existing payment never
   * runs the penalty calc - and the shortfall below is then left exactly as it was stored.
   */
  const defaultInterestAssessedRef = useRef(0)
  // Late charge computed for the Payment Received Date. Shown as a notice, never
  // auto-entered — assessing it is the servicer's decision.
  // This installment's late charge for the Payment Received Date, on its own: the server's
  // `currentLateCharge`, not its `lateCharge`, which also carries whatever is still posted unpaid
  // from earlier periods. It is never entered into Late Fees (collecting it is the servicer's
  // call), but saving a new payment books whatever of it was not entered as an unpaid late charge
  // on the loan - so it has to be this period's charge alone or the earlier periods, which already
  // have rows of their own, would be charged a second time.
  const [pendingLateCharge, setPendingLateCharge] = useState(0)
  // The Payment Received date the confirmed PAY CHARGES selection was priced to, and the charge
  // figures the amount due was last quoted from on that date. Distinct from the payoff's
  // `chargesPricedFor` ref below, which tracks the same idea for the payoff charge EDITOR: a
  // payoff re-prices its own lines automatically, while a regular payment's picked charges are
  // left as the servicer confirmed them.
  //
  // A charge carrying a rate accrues interest daily, so both are keyed to a date. The selection is
  // never re-priced behind the servicer's back - they confirmed those amounts in the picker, and
  // silently moving an agreed figure is the worse bug - so instead the notice below says the date
  // has moved and leaves reopening the picker to them. The charge figures, which are only quoted,
  // are refreshed outright whenever Fill Due re-quotes.
  const [payChargesPricedFor, setPayChargesPricedFor] = useState('')
  const [quotedLoanCharges, setQuotedLoanCharges] = useState<number[] | null>(null)

  // "Pay others": extra amounts collected on this payment and passed through to payees. Editing
  // these here queues a payable per line on the backend when the payment is applied. Seeded at
  // mount (not only from the effects below) so the opening allocation already deducts them: they
  // are part of the amount due, but the money belongs to the payees, not to interest or principal.
  const [otherPayments, setOtherPayments] = useState<OtherPaymentLine[]>(() => {
    const initial = editData.id ? editData.otherPayments : paymentInfo?.otherPayments
    return Array.isArray(initial) ? cloneDeep(initial) : []
  })
  const [payees, setPayees] = useState<Payee[]>([])

  // The charges this payoff collects, line by line. Only a payoff has these: every other payment
  // type picks charges through Pay Charges. Seeded by fillPayoff from the loan's open charges plus
  // the tenant's configured payoff fees, then the servicer's to edit.
  const [payoffCharges, setPayoffCharges] = useState<PayoffChargeLine[]>(() =>
    editData.id && Array.isArray(editData.payoffCharges) ? cloneDeep(editData.payoffCharges) : [],
  )
  // Charge category options for the lines a payoff adds, so a fee books into the same category it
  // would have had on the Charges screen (which is what decides how the collected fee splits).
  const [chargeCategories, setChargeCategories] = useState<Record<string, string>>({})
  // A new payoff seeds its charge lines from the fill data exactly once, so refreshed fill-data
  // cannot wipe lines the servicer has since edited.
  const prefilledPayoffCharges = useRef(false)
  // The payoff date the charge amounts above are priced to, and what the server quoted each line
  // at for that date. A charge carrying a rate accrues interest daily, so moving the payoff date
  // changes what it is worth; re-pricing needs to know which date the lines currently reflect, and
  // which amounts are still the server's figure rather than one the servicer has typed over.
  const chargesPricedFor = useRef('')
  const seededChargeAmounts = useRef<Record<number, number>>({})
  // The date the servicer has pinned the charge interest to, when it is not the Payment Received
  // date. A payoff is often settled on a quote the prior servicer or an earlier demand issued
  // "good through" an earlier date, and the funds arrive days later: longhorn SB2750 paid off
  // 10/16 against a quote good through 10/09, where the 22 force-placed insurance charges had
  // accrued $19.15 less. Pinning it re-prices every line at once, which is the only alternative to
  // typing over each of them. Empty means the lines follow the Payment Received date, as before.
  const [chargeInterestThrough, setChargeInterestThrough] = useState('')
  const chargeInterestThroughRef = useRef('')
  // The date the in-flight re-price was asked for, so a slow answer for a date the servicer has
  // since changed again is discarded instead of landing on top of the newer one.
  const repricingFor = useRef('')
  // A new payment prefills its Other Payments from the Servicing Information default exactly once,
  // so refreshed fill-data doesn't clobber rows the user has since edited. Already done when the
  // rows were seeded at mount above.
  const prefilledOther = useRef(otherPayments.length > 0)

  useEffect(() => {
    getLoanPayees()
      .then((res: any) => setPayees(Array.isArray(res?.payees) ? res.payees : []))
      .catch(() => setPayees([]))
    // Active categories only: a retired one must not be offered for a fee being booked now.
    getLoanChargeCategories(true)
      .then((res: any) => {
        const values: Record<string, string> = {}
        ;(Array.isArray(res) ? res : res?.data || []).forEach((item: any) => {
          const value = String(item?.value || '')
          if (value) values[value] = value
        })
        setChargeCategories(values)
      })
      .catch(() => setChargeCategories({}))
  }, [])

  useEffect(() => {
    // Skipped without the permission: the endpoint is guarded too, and asking anyway would only
    // raise a Forbidden error toast every time the modal opens.
    if (editData.id || !canTakePhonePayment) return
    let dropped = false
    // Retried, because the answer decides whether a servicer can take money over the phone at all
    // and a single GET is one connection reset away from losing it. Three attempts a second apart
    // ride out a rolling deploy; after that we say so rather than quietly answering "no".
    const ask = (attempt: number) => {
      getPhonePaymentAvailability()
        .then((res: any) => {
          if (!dropped) setPhonePaymentAvailable(!!res?.available)
        })
        .catch(() => {
          if (dropped) return
          if (attempt < 2) setTimeout(() => ask(attempt + 1), 1000 * (attempt + 1))
          else setPhonePaymentAvailable('unknown')
        })
    }
    ask(0)
    return () => {
      dropped = true
    }
  }, [editData.id, canTakePhonePayment])

  useEffect(() => {
    if (editData.id) {
      const temp = cloneDeep(inputs)
      Object.keys(temp).map((key) => {
        temp[key].value = editData[key]
      })
      setInputs(temp)
      setOtherPayments(Array.isArray(editData.otherPayments) ? editData.otherPayments : [])
      prefilledOther.current = true
      setPayoffCharges(Array.isArray(editData.payoffCharges) ? editData.payoffCharges : [])
      prefilledPayoffCharges.current = true
    }
  }, [editData])

  // New payment: seed Other Payments from the Servicing Information default (arrives via fill-data).
  useEffect(() => {
    if (editData.id || prefilledOther.current) return
    const defaults = paymentInfo?.otherPayments
    if (Array.isArray(defaults) && defaults.length) {
      setOtherPayments(cloneDeep(defaults))
      prefilledOther.current = true
    }
  }, [paymentInfo, editData])

  // On open for a NEW payment, prefill sensible defaults (dates, amounts, distribution).
  // An optional `prefill` (e.g. applying an online payment) forces the payment type and the amount
  // paid: a matching payment opens as a regular Payment; a non-matching one opens as Other Cash
  // From Borrower.
  useEffect(() => {
    if (editData.id) return
    const prefillType = prefill?.type
    if (prefillType && OTHER_CASH_TYPES.includes(prefillType)) {
      // Outside-the-schedule cash entry (mirrors selecting the type), seeded with the paid amount.
      const today = formatDate(new Date(), 'yyyy-MM-dd')
      const temp = cloneDeep(inputs)
      temp.type.value = prefillType
      Object.keys(temp).forEach((k) => {
        if (['type', 'paymentDueDate', 'paymentReceivedDate', 'paymentDepositedDate'].includes(k)) return
        const it = temp[k].inputType
        if (it === 'custom' || it === 'FileTable' || it === 'checkbox') return
        temp[k].value = ''
      })
      temp.paymentReceivedDate.value = today
      temp.paymentDepositedDate.value = today
      if (prefill?.amountPaid != null) temp.amountPaid.value = prefill.amountPaid
      setInputs(temp)
      return
    }
    fillDefaults().then(() => {
      if (prefill?.amountPaid == null) return
      // Override the scheduled amount with what the borrower actually paid, then re-split.
      setInputs((prev) => {
        const temp = cloneDeep(prev)
        temp.amountPaid.value = prefill.amountPaid as number
        redistribute(temp)
        return temp
      })
    })
  }, [])

  // All numeric coercion goes through this (a string `.toFixed()` would crash).
  const num = (v: any) => Number(removeComma(v ?? 0)) || 0

  /**
   * Everything this payment has to spend: the borrower's cash plus every bucket being drawn as a
   * source of funds.
   *
   * One helper rather than the sum written out at each site, because the sites have to agree. The
   * submit guard decides WHETHER the payment is over-applied and the adjust dialog then states by
   * how much; when the dialog's copy of the sum was missing two sources it reported a shortfall
   * over money that was already covered and asked the servicer to defer or waive it. Any source
   * added later is picked up by all of them at once.
   */
  const appliedFunds = (src: Record<string, InputType>) =>
    num(src.amountPaid?.value) + SOURCE_OF_FUNDS_KEYS.reduce((sum, key) => sum + num(src[key]?.value), 0)

  // What a Pay Charges selection collects: every charge it names, balance plus the interest accrued
  // on it. Payments saved before charges could be picked several at a time (and the payoff fill,
  // which clears every charge without naming them) carry only the collected total.
  const payChargesTotal = (value: any) => {
    const items: any[] = Array.isArray(value?.items) ? value.items : []
    if (items.length)
      return Number(items.reduce((sum, item) => sum + num(item.unpaid) + num(item.accrued), 0).toFixed(2))
    return num(value?.unpaid)
  }

  // Interest owed on the current balance, honoring the Servicing Information >
  // Interest Calculations settings: Interest Type (Dutch = accrue on balance;
  // Non-Dutch = the Amount Due) and Daily Rate Basis (30 vs actual days over
  // 360 vs 365).
  // Current outstanding principal for interest math: prefer the servicing Current UPB
  // (already net of recorded payments and construction draws — the same anchor the grid's
  // running Unpaid Balance uses), so Dutch interest accrues on the correct balance. Current UPB
  // is authoritative whenever it is provided — including exactly 0 (e.g. after a participation/UPB
  // reset). Only fall back to the amortization loan amount when Current UPB isn't set at all.
  const outstandingBalance = () => {
    const rawUpb = paymentInfo?.currentUpb
    if (rawUpb != null && rawUpb !== '') return num(rawUpb)
    let balance = num(paymentInfo?.loanAmount)
    ;(payments || []).forEach((p: Record<string, any>) => {
      balance -= num(p.principal) + num(p.amountPaid) - num(p.amountDue)
    })
    return balance
  }

  // The principal this payment is allowed to pay down. Servicing's Current UPB is authoritative
  // whenever it is set, including exactly 0 — index.tsx preserves null vs. 0 precisely so a
  // paid-off loan can be trusted. With no UPB recorded, fall back to the derived balance, and only
  // when it came out positive: a derived balance at or below zero means the balance could not be
  // reconstructed, not that the loan owes nothing. Returns null when the balance is unknown, which
  // the allocator reads as "do not cap".
  const principalCap = (): number | null => {
    const rawUpb = paymentInfo?.currentUpb
    if (rawUpb != null && rawUpb !== '') return Math.max(0, num(rawUpb))
    const derived = outstandingBalance()
    return derived > 0 ? derived : null
  }

  // Interest owed on a payoff: the per-diem from the Interest Paid Thru date to the payoff date,
  // plus any unpaid interest carried on the loan. Deliberately NOT the regular accrual period —
  // a payoff is quoted to its own date, so re-deriving it from the 30-day schedule understates it
  // and (Principal being the plug) pushes the difference into Principal.
  const payoffInterestOwed = (temp: Record<string, InputType>) => {
    const today = formatDate(new Date(), 'yyyy-MM-dd')
    const balance = outstandingBalance()
    const payoffDate = (temp.paymentReceivedDate?.value as string) || today
    const denom = String(paymentInfo?.dailyRateBasis || '365').includes('365') ? 365 : 360
    const paidThru = paymentInfo?.interestPaidThruDate as string
    // The days this payoff bills, honouring the servicer's day-count convention (resolved on the
    // server and sent with the fill data) and SIGNED, so a loan paid ahead of its payoff date
    // quotes the credit the demand gives back. payoffInterestDays holds the reasoning for both.
    const days = payoffInterestDays(paidThru, payoffDate, paymentInfo?.payoffInterestAccrualEnd)
    // Same reason the Late Fees box reads `payoffUnpaidLateChargesDue`: an itemised unpaid-interest
    // balance is already seeded into the payoff's charge editor row by row, and that editor is the
    // only thing that settles those rows, so adding the balance on top of it quoted the money
    // twice. What the server sends here is the part no row will settle, 0 once the loan itemises.
    const unpaidInterest = num(paymentInfo?.payoffUnpaidInterestDue)
    // The per-diem to the payoff date also accrues on the unpaid balances the loan charges interest
    // on (Servicing > Interest Calculations: unpaid interest, charges, late charges).
    const perDiemBase = balance + num(paymentInfo?.interestBearingBalance)
    // Same single-numerator order the backend payoff quote uses, and the same half-cent rounding,
    // so the two cannot drift a cent. payoffPerDiem holds why `toFixed` could not be used here.
    const perDiem = payoffPerDiem(perDiemBase, num(paymentInfo?.rate), days, denom)
    return {
      balance,
      interestOwed: roundToCents(perDiem + unpaidInterest),
      days,
      denom,
      compoundedUnpaidInterest: 0,
      perDiem,
      unpaidInterest,
    }
  }

  const computeInterest = (temp: Record<string, InputType>) => {
    // A payoff quotes its interest to the payoff date, not to the schedule's accrual period.
    if (temp.type?.value === 'payoff') return payoffInterestOwed(temp)
    const { rate, isDutch, dailyRateBasis, accrualPeriod, firstPaymentDate, closingDate } = paymentInfo || {}
    // For the first payment there's no prior payment to measure from, so the accrual
    // period is anchored on the loan's start date (closing/funding, else first payment).
    const firstAnchor = (closingDate || firstPaymentDate) as string
    let balance = outstandingBalance()
    // Interest on unpaid balances (Servicing > Interest Calculations): the unpaid interest, charges
    // and late charges the loan ticks join the principal as the base the rate accrues on. The
    // server works the figure out (a charge at its own rate stays out), so it is read, not rebuilt.
    // Kept out of `balance` itself, which callers also use as the outstanding principal.
    const compoundedUnpaidInterest = num(paymentInfo?.interestBearingBalance)
    if (!isDutch) {
      // Non-Dutch interest is the daily-balance interest for the accrual period (accounts for
      // dated draws), computed server-side; fall back to Amount Due if unavailable.
      // The unpaid balances are already folded in server-side (piBreakdown.compoundedUnpaidInterest).
      //
      // `interestPortion` is that interest whether or not the loan amortizes; `amount` is the
      // whole payment, which only equals it on an interest-only loan. Reading `amount` charged an
      // amortizing loan's entire level payment as interest, so it never paid down any principal.
      const nonDutchInterest = paymentInfo?.piBreakdown?.interestPortion ?? paymentInfo?.piBreakdown?.amount
      return {
        balance,
        interestOwed: nonDutchInterest != null ? num(nonDutchInterest) : num(temp.amountDue?.value),
        days: 0,
        denom: 0,
        compoundedUnpaidInterest: 0,
      }
    }
    const basis = String(dailyRateBasis || '360')
    const denom = basis.includes('365') ? 365 : 360

    // Accrual Period (Servicing > Interest Calculations) governs the numerator days:
    //   regular              → one fixed period (30 days)
    //   due_to_due           → actual days between the prior and current due dates
    //   received_to_received → actual days between the prior and current received dates
    // Falls back to Daily Rate Basis ('actual_*' ⇒ due-to-due) for loans set before
    // Accrual Period existed.
    const accrual = String(accrualPeriod || '')
    const useReceived = accrual === 'received_to_received'
    const useDueToDue = accrual === 'due_to_due' || (!accrual && basis.startsWith('actual'))
    let days = 30
    const daysBetween = (from?: string, to?: string) =>
      from && to ? Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000) : 0
    if (useReceived) {
      const curr = temp.paymentReceivedDate?.value as string
      const prevReceived = (payments && payments.length
        ? payments[payments.length - 1]?.paymentReceivedDate
        : firstAnchor) as string
      const diff = daysBetween(prevReceived, curr)
      if (diff > 0) days = diff
    } else if (useDueToDue) {
      const dueDate = temp.paymentDueDate?.value as string
      const prevDue = (payments && payments.length
        ? payments[payments.length - 1]?.paymentDueDate
        : firstAnchor) as string
      const diff = daysBetween(prevDue, dueDate)
      if (diff > 0) days = diff
    }
    // One numerator, then the divisions: (days / denom) evaluated on its own lands just under
    // the true fraction (30/360 -> 0.08333333333333333), so a period sitting exactly on a half
    // cent rounded DOWN here while the backend rounded UP. $227,000 at 12.99% billed $2,457.27
    // on this screen against Servicing Information's $2,457.28. This is the operation order the
    // backend uses throughout (balance * rate * days / basis / 100), so the two screens agree.
    // `roundToCents`, not `toFixed`, for the same reason: a figure landing exactly on a half cent
    // has to round UP the way the billed P&I does, or the cent falls through into Principal.
    // $119,000 at 12.99% accrues exactly 1288.175 a month, which `toFixed(2)` calls $1,288.17
    // against the $1,288.18 the loan is actually billed, and Principal (the plug for whatever
    // interest leaves behind) collected the stray cent on an interest-only loan.
    //
    // This read a file-local `Math.round((n + Number.EPSILON) * 100) / 100`, which quotes the same
    // figure as no nudge at all: EPSILON is the gap between 1 and its successor, so adding it to a
    // money figure is a no-op - the gap at 1288.175 is a thousand times larger. It only appeared
    // to work where multiplying by 100 happened to land back on the midpoint, and it quoted 2,003
    // of the 25,123 exact half cents in a 1.32M-figure accrual sweep a penny light. `roundToCents`,
    // the port of the server's own helper, quotes all 25,123 the way the server stores them.
    const interestOwed = roundToCents(((balance + compoundedUnpaidInterest) * num(rate) * days) / 100 / denom)
    return { balance, interestOwed, days, denom, compoundedUnpaidInterest }
  }

  // The overage this allocator last wrote into Suspense. Kept so a later re-split can replace
  // its own figure without wiping an amount the servicer typed into that field themselves.
  const autoSuspenseRef = useRef(0)
  const manualSuspense = (temp: Record<string, InputType>) =>
    Math.max(0, Number((num(temp.suspense?.value) - autoSuspenseRef.current).toFixed(2)))

  // Recompute Interest / Unpaid Interest / Principal from the current fixed
  // allocations (escrow, MI, fees, charges) and the applied amount
  // (Amount Paid + Suspense + Impound). Synchronous — no API call.
  // Funds available to split between interest and principal after the fixed
  // allocations (escrow, MI, fees, charges) are taken from the applied amount.
  const availableForInterest = (temp: Record<string, InputType>) => {
    const amountApplied = appliedFunds(temp)
    const escrowsTotal = num(temp.mortgage?.value) + num(temp.escrows?.value)
    const deductions =
      escrowsTotal +
      num(temp.buydown?.value) +
      num(temp.lateFee?.value) +
      num(temp.servicingFee?.value) +
      num(temp.defaultInterest?.value) +
      payChargesTotal(temp.payCharges?.value) +
      // Collected for third parties and paid straight through to them. A payoff handing the
      // borrower back its own overage is NOT that: the refund is paid out of the Suspense this
      // payment credits, so deducting it here took the same money twice and left Suspense holding
      // less than the payout it has to fund. See payoffRefund.
      otherPaymentsCollected(otherPayments, temp.type?.value === 'payoff') +
      // Money the servicer has parked in Suspense by hand is held, not available to pay the
      // loan down. Only their own entry counts: the overage this allocator writes there is
      // what's left AFTER the split, so counting it would feed back into its own input.
      manualSuspense(temp)
    return amountApplied - deductions
  }

  /** True when this loan capitalizes uncollected interest into principal (negative amortization). */
  const capitalizesInterest = paymentInfo?.unpaidInterestAppliesTo === 'principal'

  /**
   * How far a payoff falls short of what it has to collect, or 0 when it covers it.
   *
   * The mirror of the overage: money paid OVER the amount owed is held in Suspense, money short of
   * it is carried as a negative Suspense, and the note under that field names both. Drives the
   * allocator too (`applyPrincipal`) - before it did, a payoff keyed from a wire that came in light
   * read on screen exactly like one that paid in full, with Principal quietly short by the shortage.
   *
   * Measured against Amount Due, which is the payoff the quote filled and which every later edit
   * keeps in step (a charge line, an other-payment row, re-quoting on a new payoff date all move
   * it), and against `appliedFunds` so every bucket being drawn counts the same way it does for the
   * allocator and the save guard. A fresh quote fills the two to the same figure, so this is 0
   * until the servicer edits one of them.
   */
  const payoffShortfall = (src: Record<string, InputType>) => {
    if (src.type?.value !== 'payoff') return 0
    const owed = num(src.amountDue?.value)
    if (!owed) return 0
    return Math.max(0, Number((owed - appliedFunds(src)).toFixed(2)))
  }

  /**
   * Record a change in what this payment is collecting: Amount Due moves by `delta`, and Amount
   * Paid moves with it only while the two are still the quote's own figures.
   *
   * Every edit to what the borrower owes - a charge line, a pass-through payee, the servicing fee -
   * has always moved both, which is right on a fresh quote: the cash to collect is whatever is
   * owed. On a payoff the servicer eventually keys the cash that actually arrived, and from then on
   * moving it would ask them to retype the one figure they are certain of and leave the difference
   * reading as an overage or a shortfall that is not real. `amountPaidFollowsOwed` is that rule.
   *
   * Call BEFORE Amount Due is otherwise touched: whether the payoff is already over or short is
   * measured against the Amount Due standing when the edit was made.
   */
  const applyOwedDelta = (temp: Record<string, InputType>, delta: number) => {
    if (!delta) return temp
    const follows = amountPaidFollowsOwed(
      temp.type?.value === 'payoff',
      payoffShortfall(temp),
      autoSuspenseRef.current,
    )
    temp.amountDue.value = roundToCents(num(temp.amountDue.value) + delta)
    if (follows) temp.amountPaid.value = roundToCents(num(temp.amountPaid.value) + delta)
    return temp
  }

  // Put the funds left after interest into Principal, but never more principal than the loan
  // actually owes. Anything beyond the balance is not principal: paying more than the balance
  // cannot reduce it below zero, and on a payoff the surplus is money owed back to the borrower
  // (which is what the payoff demand tells them). Left in Principal it silently overstates the
  // principal collected on the payment, in the investor distribution and on the 1098, while the
  // balance itself just floors at zero so nothing on screen shows the money. Suspense is where the
  // rest of the platform holds it — the payment import's `overpayment` option has always defaulted
  // there, and suspense is deliberately excluded from participant distributions.
  const applyPrincipal = (temp: Record<string, InputType>, forPrincipal: number) => {
    // A payoff that comes up short does NOT pay down less principal. The demand quoted a principal
    // figure and the loan is being closed out against it, so Principal holds that quote and the
    // deficiency is carried as a NEGATIVE Suspense - the same field the overage lands in, read the
    // other way round. Total Credited counts Suspense, so it still ties back to the funds applied.
    // Left as the plug, Principal silently absorbed the whole shortage: a $756.16 light wire read
    // as $756.16 less principal collected, which is the figure the investor distribution, the 1098
    // and principal paid-to-date all take, and nothing on screen said where the money went.
    const quotedPrincipal = payoffShortfall(temp) > 0 ? principalCap() : null
    const cap = overpaymentAppliesTo === 'principal' ? null : principalCap()
    const principal = quotedPrincipal ?? Math.max(0, cap == null ? forPrincipal : Math.min(forPrincipal, cap))
    // Signed only on a short payoff. Every other split still floors at zero rather than pushing a
    // negative into Suspense - an underfunded regular payment allocates less, it does not owe.
    const remainder = forPrincipal - principal
    const overage = Number((quotedPrincipal != null ? remainder : Math.max(0, remainder)).toFixed(2))
    temp.principal.value = Number(principal.toFixed(2))
    if (temp.suspense) {
      const held = Number((manualSuspense(temp) + overage).toFixed(2))
      temp.suspense.value = held !== 0 ? held : ''
    }
    autoSuspenseRef.current = overage
    return temp
  }

  /**
   * Default interest this payment assessed and did not collect, into its own field.
   *
   * The assessed premium is auto-filled into Default Interest, so the gap opens only when the
   * servicer lowers or clears it - which is the one lever they have when collecting the premium
   * ahead of scheduled interest would leave the payment short. Before this bucket existed that
   * decision simply forgave the money: nothing recorded it, on this screen or any other route.
   *
   * Its own balance and never netted against the interest shortfall beside it: the two are owed
   * under different terms - the note rate against the default spread - and a servicer waives one
   * without waiving the other.
   *
   * Left untouched when the assessed figure is unknown (0), so editing an existing payment keeps
   * the shortfall it was saved with instead of having it zeroed by a re-split.
   */
  const applyUnpaidDefaultInterest = (temp: Record<string, InputType>) => {
    const assessed = defaultInterestAssessedRef.current
    if (!temp.unpaidDefaultInterest || assessed <= 0) return temp
    temp.unpaidDefaultInterest.value = roundToCents(Math.max(0, assessed - num(temp.defaultInterest?.value)))
    return temp
  }

  // Given a fixed Interest amount, derive the shortfall vs. what's owed and where it goes, per the
  // servicing "Unpaid Interest Charges Applies" setting. Either way the shortfall is recorded -
  // only which balance carries it changes:
  //   'unpaidInterest' (default) → tracked as an Unpaid Interest receivable. The balance does not
  //      move, and Principal takes only funds beyond the interest owed.
  //   'principal' (negative amortization) → capitalized into the principal balance, so the loan
  //      grows and from the next period interest accrues on the larger balance.
  //
  // Capitalized interest is its own field rather than negative Principal: Principal here means
  // principal *collected*, which is what participants are distributed and what principal
  // paid-to-date reports, so a negative there would read as income nobody received. Neither field
  // takes funds away from Principal - a shortfall means there were none left to take.
  const splitFromInterest = (temp: Record<string, InputType>, interest: number) => {
    const { interestOwed } = computeInterest(temp)
    const available = availableForInterest(temp)
    const shortfall = Number(Math.max(0, interestOwed - interest).toFixed(2))
    temp.interest.value = Number(interest.toFixed(2))
    temp.unpaidInterest.value = capitalizesInterest ? 0 : shortfall
    if (temp.capitalizedInterest) temp.capitalizedInterest.value = capitalizesInterest ? shortfall : 0
    applyUnpaidDefaultInterest(temp)
    // Interest the servicer has given up is no longer money this payment has to find, so it comes
    // off Amount Due - by the CHANGE in the waiver, since part of it may already be off. Without
    // this a payoff settled for less interest read short by every dollar waived and parked that in
    // Suspense as a deficiency the borrower did not owe: longhorn SB2750 waived $98,052.54 of
    // interest and $10,952.46 of late fees against a wire that covered the rest to the cent, and
    // the Suspense field never moved off -$109,024.15.
    const waived = waivedInterest(shortfall, waiveUnpaidInterestRef.current, capitalizesInterest)
    applyOwedDelta(temp, -Number((waived - waivedInterestOff.current).toFixed(2)))
    waivedInterestOff.current = waived
    // Capitalized interest is not cash - it is added to the balance - so it never reduces the
    // funds left for Principal. A tracked receivable does, holding that much back against it; a
    // waived one does not, there being nothing left to hold it against.
    const carried = capitalizesInterest ? 0 : Number((shortfall - waived).toFixed(2))
    return applyPrincipal(temp, Math.max(0, available - interest - carried))
  }

  const redistribute = (temp: Record<string, InputType>) => {
    const { interestOwed } = computeInterest(temp)
    // With a waiver in force the Interest field is the servicer's OWN figure - the waiver is the
    // decision to forgive the rest of what is owed - so a re-split must not re-derive it from the
    // funds on hand and throw that decision away. Re-deriving it put the whole interest back, which
    // took the waiver back off Amount Due the moment anything else on the payment was edited.
    const interest =
      waiveUnpaidInterestRef.current !== null
        ? num(temp.interest?.value)
        : allocatableInterest(interestOwed, availableForInterest(temp))
    return splitFromInterest(temp, interest)
  }

  // Breakdown of how the Interest field was derived, for the info modal.
  const getInterestBreakdown = () => {
    const { rate, isDutch, dailyRateBasis, accrualPeriod } = paymentInfo || {}
    const { balance, interestOwed, days, denom, compoundedUnpaidInterest } = computeInterest(inputs)
    const amountApplied = appliedFunds(inputs)
    // Read the allocator's own arithmetic rather than restating it, so the explanation cannot
    // drift from the split it is explaining.
    const available = availableForInterest(inputs)
    const deductions = Number((amountApplied - available).toFixed(2))
    const interest = allocatableInterest(interestOwed, available)
    const unpaidInterest = Number(Math.max(0, interestOwed - interest).toFixed(2))
    return {
      isDutch: !!isDutch,
      rate: num(rate),
      balance,
      interestOwed,
      amountApplied,
      deductions,
      available,
      interest,
      unpaidInterest,
      days,
      denom,
      compoundedUnpaidInterest: compoundedUnpaidInterest || 0,
      dailyRateBasis: String(dailyRateBasis || ''),
      accrualPeriod: String(accrualPeriod || ''),
      defaultInterest: num(inputs.defaultInterest?.value),
      defaultRate: defaultRateInfo.rate,
      defaultDays: defaultRateInfo.days,
    }
  }

  /**
   * The payoff's charge lines as they go to the server: only on a payoff, and only lines that name
   * a charge and carry an amount, so a blank row left behind never books a $0 fee. Sent empty for
   * every other payment type, which leaves the server's charge handling untouched.
   */
  const submittedPayoffCharges = (): PayoffChargeLine[] => {
    if (inputs.type?.value !== 'payoff') return []
    return payoffCharges.filter(
      (row) => String(row.title || '').trim() && Number(removeComma(row.amount ?? 0) || 0) > 0,
    )
  }

  // The payment payload as it currently stands, without running validation — used both to apply the
  // payment and to preview the participant split before applying.
  const collectPaymentData = () => {
    const data: Record<string, any> = {}
    Object.keys(inputs).forEach((key) => {
      if (key == 'totalCredited' || key.startsWith('custom')) return
      data[key] = inputs[key].value
    })
    data.suspense = inputs.suspense?.value
    data.payer = inputs.payer?.value ?? 'Borrower'
    return data
  }

  /**
   * Turn the waiver on the uncollected interest on or off. Turning it on asks for a reason, which
   * is optional in the same way the Charges screen's waiver is - the amount, the date and the user
   * are the trail that matters.
   */
  const onToggleWaiveInterest = async (shortfall: number) => {
    // Re-split on the way in and out: the waiver decides whether the shortfall is held back from
    // Principal and whether it is still part of Amount Due, and `splitFromInterest` reads it from
    // the ref so the answer is the new one rather than the state this closure captured.
    const setWaiver = (reason: string | null) => {
      waiveUnpaidInterestRef.current = reason
      setWaiveUnpaidInterest(reason)
      // Re-split on the Interest the servicer has entered, not on what the funds would allocate:
      // the waiver is about the shortfall under that very figure, and re-deriving it here would
      // collect the whole interest again and leave nothing waived.
      setInputs(splitFromInterest(cloneDeep(inputs), num(inputs.interest?.value)))
    }
    if (waiveUnpaidInterest !== null) {
      setWaiver(null)
      return
    }
    const content = (
      <div className="mb-4 text-[16px] text-left">
        <p className="mb-2">
          Waive <span className="font-semibold">${thousandSeperator(shortfall.toFixed(2))}</span> of interest this
          payment does not cover?
        </p>
        <p className="text-[13px] text-gray-500">
          The interest is still booked against the loan and then waived, so the Charges screen keeps the record that
          it was charged and forgiven. Nothing further is billed, collected or quoted on it. Leave it as Unpaid
          Interest instead if the borrower still owes it.
        </p>
      </div>
    )
    const reason: any = await prompt(content, {
      placeholder: 'Why is it being waived? (optional)',
      titleOk: 'Waive Interest',
      rows: 3,
    })
    if (reason === false || reason == null) return
    setWaiver(typeof reason === 'string' ? reason : '')
  }

  /**
   * The part of this installment's late charge the servicer has not entered under Late Fees.
   *
   * Saving a NEW regular payment books this as an unpaid late charge on the loan, so skipping the
   * fee defers it instead of dropping it: before this, the notice promised the charge "will still
   * be due after the payment is applied" and nothing wrote it anywhere, so the Unpaid Late Charges
   * balance never moved and the next period's calculation - which adds back only posted rows - did
   * not carry it either. The charge simply vanished.
   *
   * Only a new payment, and only a regular one. Editing an existing payment would book the charge
   * again on every save, and a payoff settles the whole delinquency through its own accrued late
   * charges rather than one installment's.
   */
  const lateChargeShortfall = () => {
    if (editData.id || inputs.type?.value !== 'payment') return 0
    return Math.max(0, Number((pendingLateCharge - num(inputs.lateFee?.value)).toFixed(2)))
  }

  // Collect the whole assessed charge from this payment instead of carrying it: fill Late Fees and
  // re-split, so the fee comes out of the funds applied (`lateFee` is in redistribute's key list,
  // so interest shrinks and any shortfall shows as Unpaid Interest - the totals still tie out to
  // Amount Paid).
  const onPayLateCharge = () => {
    const temp = cloneDeep(inputs)
    temp.lateFee.value = pendingLateCharge
    redistribute(temp)
    setInputs(temp)
  }

  const onModalOk = () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}

    Object.keys(inputs).map((key) => {
      if (key == 'totalCredited' || key.startsWith('custom')) return
      newInputs[key].error = InputValidate({ ...newInputs[key], value: newInputs[key].value })
      if (newInputs[key].error?.length) hasError = true
      data[key] = newInputs[key].value
    })
    data.suspense = inputs.suspense?.value
    data.payer = inputs.payer?.value ?? 'Borrower'
    // The user reviewed and edited the participant split — apply exactly those rows instead of the
    // automatic ownership split. Carried through the over-allocation adjust flow too (via pendingData).
    if (distributions) data.participantDistributions = distributions
    // Drop blank rows (no payee or no amount) so an empty line never queues a $0 payable.
    data.otherPayments = otherPayments.filter(
      (r) => String(r.payeeName || '').trim() && Number(removeComma(r.amount ?? 0) || 0) > 0,
    )
    data.payoffCharges = submittedPayoffCharges()
    setInputs(newInputs)
    if (hasError) return

    const totalCredited = Number(removeComma(inputs.totalCredited.value) || 0)
    // Every bucket drawn as a source of funds counts on top of the borrower's cash. The adjust
    // dialog below is handed the SAME figure, so what opens it and what it states cannot disagree.
    const amountApplied = appliedFunds(inputs)

    if (totalCredited > amountApplied) {
      setPendingData(data)
      setShowAdjustModal(true)
      return
    }

    // The servicer chose to forgive the interest this payment leaves uncollected rather than carry
    // it. Sent as a waiver of the Unpaid Interest the save books, not as a change to the allocation
    // - what the payment collected is the same either way.
    const unpaidInterest = Number(removeComma(data.unpaidInterest) || 0)
    const waivers =
      waiveUnpaidInterest !== null && unpaidInterest > 0
        ? { unpaidInterest: { reason: waiveUnpaidInterest } }
        : undefined
    // The assessed late charge the servicer did not collect is booked by the SERVER, on the one
    // path every payment goes through - see LoanPaymentsService.bookUncollectedLateCharge. It
    // used to be booked here, which meant it only happened for a payment typed into this screen;
    // a payment taken by phone, drafted by the ACH run, imported, or paid in the borrower portal
    // raised no charge at all.
    onOk(editData.id, data, undefined, waivers)
  }

  /**
   * Validate the payment exactly as saving would, then hand the allocation to the phone-payment
   * modal. The backend charges the card / bank account first and only posts this payment if the
   * charge is accepted, so nothing is saved here.
   */
  const onTakePhonePayment = () => {
    const newInputs = cloneDeep(inputs)
    let hasError = false
    const data: Record<string, any> = {}
    Object.keys(newInputs).forEach((key) => {
      if (key == 'totalCredited' || key.startsWith('custom')) return
      newInputs[key].error = InputValidate({ ...newInputs[key], value: newInputs[key].value })
      if (newInputs[key].error?.length) hasError = true
      data[key] = newInputs[key].value
    })
    setInputs(newInputs)
    if (hasError) return

    data.suspense = inputs.suspense?.value
    data.payer = inputs.payer?.value ?? 'Borrower'
    if (distributions) data.participantDistributions = distributions
    data.otherPayments = otherPayments.filter(
      (r) => String(r.payeeName || '').trim() && Number(removeComma(r.amount ?? 0) || 0) > 0,
    )
    data.payoffCharges = submittedPayoffCharges()

    const amountPaid = Number(removeComma(inputs.amountPaid.value) || 0)
    if (!(amountPaid > 0)) {
      toast('Enter the Amount Paid before taking the payment by phone.', { type: 'error' })
      return
    }

    // An over-allocated payment has to be reconciled before money is taken — the adjust flow is
    // part of saving, and there is no sensible way to run it after the card has been charged.
    const totalCredited = Number(removeComma(inputs.totalCredited.value) || 0)
    const amountApplied = appliedFunds(inputs)
    if (totalCredited > amountApplied) {
      toast('This payment credits more than it applies. Adjust the allocation before charging.', { type: 'error' })
      return
    }

    setPhonePaymentData(data)
  }

  const onChange = (key: string, value: any) => {
    const temp = cloneDeep(inputs)
    temp[key].value = value
    if (key === 'type') {
      if (OTHER_CASH_TYPES.includes(value)) {
        // Other Cash (from Borrower/Lender): outside the payment schedule — clear the
        // schedule-derived amounts and the interest/principal split; keep the dates so
        // it's recorded as a dated cash entry the user allocates manually.
        const today = formatDate(new Date(), 'yyyy-MM-dd')
        Object.keys(temp).forEach((k) => {
          if (['type', 'paymentDueDate', 'paymentReceivedDate', 'paymentDepositedDate'].includes(k)) return
          const it = temp[k].inputType
          if (it === 'custom' || it === 'FileTable' || it === 'checkbox') return
          temp[k].value = ''
        })
        temp.paymentReceivedDate.value = today
        temp.paymentDepositedDate.value = today
        setInputs(temp)
        return
      }
      // Regular Payment / Payoff: prefill defaults. Pass `temp` (which already has the
      // new type) so the fill isn't blocked by the not-yet-committed prior value.
      if (!editData.id) {
        if (value === 'payoff') fillPayoff(temp)
        else fillDefaults(temp)
        return
      }
      setInputs(temp)
      return
    }
    // Manually editing Interest is an override: keep the entered amount and re-derive
    // Unpaid Interest (the shortfall vs. what's owed) and Principal from it. So setting
    // Interest to 0 moves the full owed amount to Unpaid Interest.
    // Payoff: recompute the full payoff when the payoff (received) date changes, so
    // the per-diem interest is quoted to that date.
    if (temp.type?.value === 'payoff' && key === 'paymentReceivedDate') {
      fillPayoff(temp, { keepDates: true })
      return
    }
    if (!OTHER_CASH_TYPES.includes(temp.type?.value) && key === 'interest') {
      splitFromInterest(temp, num(value))
      // Keep what was typed. The split writes back the rounded number, which on every keystroke
      // turned "12." into "12" and "12.50" into "12.5", so a decimal could never be entered.
      temp.interest.value = value
      setInputs(temp)
      return
    }
    // Manually editing the Servicing Fee changes what the borrower owes, so Amount Due
    // and Amount Paid move with it (same as Other Payments). No re-split needed: the fee
    // is deducted again in availableForInterest, so interest/principal are unchanged.
    if (!OTHER_CASH_TYPES.includes(temp.type?.value) && key === 'servicingFee') {
      const delta = Number((num(value) - num(inputs.servicingFee?.value)).toFixed(2))
      if (delta) {
        temp.amountDue.value = Number((num(temp.amountDue.value) + delta).toFixed(2))
        temp.amountPaid.value = Number((num(temp.amountPaid.value) + delta).toFixed(2))
      }
      setInputs(temp)
      return
    }
    // The penalty (late charge + default interest) is driven by the Payment Received
    // Date — recompute it whenever that date changes, then re-split.
    if (!OTHER_CASH_TYPES.includes(temp.type?.value) && key === 'paymentReceivedDate') {
      // The charge figures were quoted for the old date, and each charge carrying a rate is worth
      // a different amount on the new one. Drop the quote rather than show it against a date it
      // was not priced for; Fill Due asks again. The confirmed Pay Charges selection is left
      // alone on purpose - see chargesSelectionStale, which tells the servicer it has moved.
      setQuotedLoanCharges(null)
      applyPenalty(temp).then(() => {
        redistribute(temp)
        setInputs(temp)
      })
      return
    }
    // Recalculate the Applies To distribution whenever a source-of-funds field changes,
    // Late Fees on a payoff is what the servicer has settled for, not a schedule's figure: waiving
    // a late charge means the payoff collects that much less, so Amount Due follows it the way it
    // follows a charge line or a pass-through payee. A regular payment's amount due is the
    // schedule's and does not move. Taken before the re-split below, which re-reads Amount Due.
    if (temp.type?.value === 'payoff' && key === 'lateFee') {
      applyOwedDelta(temp, Number((num(value) - num(inputs.lateFee?.value)).toFixed(2)))
    }
    // when the received/due date changes (those drive the Accrual Period day-count), or
    // when a Late Fee is keyed in (it is collected out of the applied funds, so interest/
    // principal shrink to match) — never for an outside-the-schedule cash entry.
    if (
      !OTHER_CASH_TYPES.includes(temp.type?.value) &&
      [
        'amountPaid',
        'sourceSuspense',
        'sourceImpound',
        'sourceInterestReserve',
        'sourceConstructionReserve',
        'sourceEscrowRepairHoldback',
        'sourceTrustBalance',
        'paymentReceivedDate',
        'paymentDueDate',
        'lateFee',
      ].includes(key)
    ) {
      redistribute(temp)
    }
    setInputs(temp)
  }

  const onFillDate = async () => {
    const temp = cloneDeep(inputs)
    const { nextPaymentDate } = paymentInfo
    const currentDate = formatDate(new Date(), 'yyyy-MM-dd')
    temp.paymentReceivedDate.value = currentDate
    temp.paymentDepositedDate.value = currentDate

    if (nextPaymentDate) temp.paymentDueDate.value = formatDate(nextPaymentDate, 'yyyy-MM-dd')
    // Dates drive the Accrual Period day-count, so refresh the interest allocation.
    redistribute(temp)
    setInputs(temp)
  }

  // Recompute the penalty (late charge + default interest) for the current
  // Payment Received Date, and stash the default-rate details for the "?" breakdown.
  const applyPenalty = async (temp: Record<string, InputType>) => {
    const { currentLateCharge, description, defaultRate, diffDaysForDefaultRate } =
      await getLoanPaymentLateChargeCalculation(
        temp.paymentReceivedDate.value as string,
        paymentInfo.nextPaymentDate,
      )
    const { rate } = paymentInfo
    const balance = outstandingBalance()
    // The late charge is the servicer's call, not an automatic deduction: surface it as
    // a notice (and in the field tooltip) instead of entering it, so the payment applies
    // in full and the fee can be keyed into Late Fees only if it should be collected now.
    setPendingLateCharge(num(currentLateCharge))
    temp.lateFee.tooltip = description
    temp.defaultInterest.value = defaultRate
      ? roundToCents(((balance * (defaultRate - rate)) / 100 / 360) * diffDaysForDefaultRate)
      : 0
    defaultInterestAssessedRef.current = num(temp.defaultInterest.value)
    applyUnpaidDefaultInterest(temp)
    setDefaultRateInfo({ rate: num(defaultRate), days: num(diffDaysForDefaultRate) })
  }

  /**
   * The payment fill data priced to `receivedDate`, or null to keep what the screen already has.
   *
   * Only the charge figures in it move with the date (each charge carrying a rate accrues daily),
   * so this is read for those and the amount due that counts them. A whole `YYYY-MM-DD` only - the
   * backend reads a partial value as "no date" and quotes today, so sending one would answer with
   * a figure the screen could not explain. A failed read returns null rather than throwing, so a
   * backend that cannot answer leaves the previous quote standing instead of blanking Amount Due.
   */
  const datedFillData = async (receivedDate: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(receivedDate || '')) return null
    return getLoanPaymentFillData(receivedDate, paymentInfo.nextPaymentDate).catch(() => null)
  }

  const onFillDue = async () => {
    const temp = cloneDeep(inputs)
    const { isDutch, rate } = paymentInfo
    const receivedDate = inputs.paymentReceivedDate.value as string
    const [{ currentLateCharge, defaultRate, diffDaysForDefaultRate }, dated] = await Promise.all([
      getLoanPaymentLateChargeCalculation(receivedDate, paymentInfo.nextPaymentDate),
      // The amount due priced to the received date, because the charges in it accrue interest
      // daily. `paymentInfo` is fetched once when the screen opens and carries no date, so filling
      // from it quoted today's charge interest however far back the payment was keyed: on a loan
      // with ten force-placed insurance charges at 18%, a month back read $33.60 over and three
      // months back $103.09 over. Asked for only here, on an explicit Fill Due, rather than on
      // every keystroke of the date - a dated refetch rebuilds the amortization schedule and the
      // HUD, which is why the payoff re-prices through its own endpoint instead.
      datedFillData(receivedDate),
    ])
    const amountDue = num(dated?.amountDue ?? paymentInfo.amountDue)
    // The charge figures that amount due just counted, so the breakdown under Pay Charges names
    // the same ones. Left alone when the re-quote failed: the previous quote is a better answer
    // than none.
    if (Array.isArray(dated?.loanCharges)) setQuotedLoanCharges(dated.loanCharges.map((v: any) => num(v)))
    const balance = outstandingBalance()
    const defaultInterest = defaultRate
      ? roundToCents(((balance * (defaultRate - rate)) / 100 / 360) * diffDaysForDefaultRate)
      : 0
    setDefaultRateInfo({ rate: num(defaultRate), days: num(diffDaysForDefaultRate) })
    setPendingLateCharge(num(currentLateCharge))
    temp.defaultInterest.value = defaultInterest
    defaultInterestAssessedRef.current = defaultInterest
    applyUnpaidDefaultInterest(temp)
    // Only a late fee the servicer actually entered is part of what is due — the computed
    // charge is advisory (the notice below the form) until they decide to collect it.
    temp.amountDue.value = isDutch
      ? Number((amountDue + num(temp.lateFee.value) + defaultInterest).toFixed(2))
      : Number((calculateNonDutchAmountDue(inputs.paymentDueDate.value) + sumOtherPayments(otherPayments)).toFixed(2))
    setInputs(temp)
  }

  const onFillPaid = () => {
    const temp = cloneDeep(inputs)
    temp.amountPaid.value = temp.amountDue.value
    // Recalculate the Applies To distribution from the new amount (regular payments).
    if (!OTHER_CASH_TYPES.includes(temp.type?.value)) redistribute(temp)
    setInputs(temp)
  }

  /**
   * Editing the payoff's charge lines moves what the borrower owes, so the Charges figure, the
   * amount due and the amount paid all follow it.
   *
   * `payCharges` is kept in step with the same total because the allocator already deducts charges
   * through it (and Total Credited already adds them): writing the total there means the split
   * balances with no further arithmetic here, exactly as it did when the payoff filled one lump sum.
   * The lines themselves go to the server on apply, which books the new fees and replaces this
   * total with the real per-charge items.
   */
  const onPayoffChargesChange = (rows: PayoffChargeLine[]) => {
    const total = sumPayoffCharges(rows)
    const delta = Number((total - sumPayoffCharges(payoffCharges)).toFixed(2))
    setPayoffCharges(rows)
    setInputs((prev) => {
      const temp = cloneDeep(prev)
      temp.charges.value = total
      temp.payCharges.value = total > 0 ? { unpaid: total, accrued: 0 } : {}
      applyOwedDelta(temp, delta)
      // Re-split so the funds this change freed up (or took away) land back in interest and
      // principal instead of leaving Total Credited out of step with Amount Paid.
      return redistribute(temp)
    })
  }

  /**
   * Pin the date the charge interest is quoted through, and re-price every line to it at once.
   *
   * A charge carrying a rate is worth a day less of interest for every day earlier it is quoted,
   * and a payoff is routinely settled on a demand issued good through a date before the funds
   * arrived. The lines otherwise follow the Payment Received date, which is the day the money came
   * in, not the day the figures were agreed - leaving the servicer to type over every line by hand
   * (longhorn SB2750: 22 force-placed insurance charges, $19.15 between the two dates).
   *
   * Clearing the date puts them back on the Payment Received date. A line the servicer has typed
   * over keeps their own figure, exactly as it does when the payment date moves.
   */
  const onChargeInterestThroughChange = async (date: string) => {
    chargeInterestThroughRef.current = date
    setChargeInterestThrough(date)
    const repriced = await repricedChargeLines(
      payoffCharges,
      date || (inputs.paymentReceivedDate?.value as string) || '',
    )
    if (repriced) onPayoffChargesChange(repriced)
  }

  /**
   * Editing the pass-through payees changes what the borrower owes, so Amount Due moves with the
   * total (and Amount Paid with it, on everything but a payoff whose cash is already keyed - see
   * `applyOwedDelta`). The allocation itself needs no re-split: the extra cash is deducted again
   * in `availableForInterest`, so what's left for interest and principal is unchanged.
   *
   * Measured on what the lines COLLECT, which on a payoff leaves out the borrower's own overage
   * being handed back: that money is already in Amount Paid and already held in Suspense, so
   * counting it moved both figures off the quote and the wire. See payoffRefund.
   */
  const onOtherPaymentsChange = (rows: OtherPaymentLine[]) => {
    const isPayoff = inputs.type?.value === 'payoff'
    const delta = Number(
      (otherPaymentsCollected(rows, isPayoff) - otherPaymentsCollected(otherPayments, isPayoff)).toFixed(2),
    )
    setOtherPayments(rows)
    if (!delta || OTHER_CASH_TYPES.includes(inputs.type?.value)) return
    setInputs((prev) => applyOwedDelta(cloneDeep(prev), delta))
  }

  const onFillAllSuspense = () => {
    const temp = cloneDeep(inputs)
    const totalCreditedVal = temp.totalCredited.value
    temp.suspense.value = totalCreditedVal
    ;[
      'principal',
      'interest',
      'mortgage',
      'escrows',
      'escrowsTotal',
      'buydown',
      'lateFee',
      'servicingFee',
      'defaultInterest',
    ].forEach((key) => {
      if (temp[key]) temp[key].value = 0
    })
    // Default Interest was just zeroed along with the rest, so the whole assessed premium is now
    // uncollected and owed. Set here because this handler deliberately bypasses the allocator.
    applyUnpaidDefaultInterest(temp)
    temp.payCharges.value = {}
    // Everything now sits in Suspense because the servicer asked for it, not because the
    // allocator put an overage there — so none of it is this allocator's to reclaim.
    autoSuspenseRef.current = 0
    setInputs(temp)
  }

  const onFillEscrow = async (base?: Record<string, InputType>) => {
    const temp = cloneDeep(base || inputs)
    const { mortgageInsurance, escrowAmount, servicingFee } = paymentInfo
    // Fixed allocations from servicing + the penalty calc; interest/principal
    // (and any Unpaid Interest) are then derived by redistribute().
    temp.mortgage.value = mortgageInsurance
    temp.escrows.value = escrowAmount
    // Borrower servicing fee (Servicing Information > Servicing Fee) — billed on
    // every regular payment and already included in the servicing Amount Due.
    if (temp.servicingFee) temp.servicingFee.value = num(servicingFee) || ''
    await applyPenalty(temp)
    redistribute(temp)
    setInputs(temp)
  }

  /**
   * The late charges a payoff quoted to this received date has run up: one per installment that has
   * fallen due and cleared its grace period since the last paid one. Read from the server rather
   * than derived here, because it is the same calculation the payoff demand quotes, so the demand
   * and the payment that settles it cannot drift. Falls back to nothing: a prefill must still
   * produce a quote the servicer can work from.
   */
  const accruedLateCharges = async (temp: Record<string, InputType>) => {
    try {
      const res = await getLoanPaymentLateChargeCalculation(
        temp.paymentReceivedDate?.value as string,
        temp.paymentDueDate?.value as string,
      )
      return { total: num(res?.accruedLateCharges), installments: num(res?.accruedLateInstallments) }
    } catch {
      return { total: 0, installments: 0 }
    }
  }

  /**
   * Remember what the server quoted each existing charge at, and the payoff date it quoted them
   * for. That is what tells a later re-price which lines are still the server's figure: a line the
   * servicer has typed over is theirs, because a payoff is negotiated, and silently moving an
   * agreed figure because the date changed would be the worse bug.
   */
  const rememberChargePrices = (quoted: PayoffChargeLine[], payoffDate: string) => {
    const amounts: Record<number, number> = {}
    quoted.forEach((line) => {
      const id = Number(line?.id)
      if (id > 0) amounts[id] = num(line?.amount)
    })
    seededChargeAmounts.current = amounts
    chargesPricedFor.current = payoffDate
  }

  /**
   * The charge lines re-priced to `payoffDate`, or null to keep the ones passed in.
   *
   * Only the charges the loan already owes move: a fee the payoff adds carries no interest, and a
   * line the servicer edited is left exactly as typed. Returns null when there is nothing to do -
   * no lines, no date, already priced to that date - and also when the read fails, so a backend
   * that cannot answer leaves the quote the screen had rather than zeroing its charges.
   */
  const repricedChargeLines = async (
    lines: PayoffChargeLine[],
    payoffDate: string,
  ): Promise<PayoffChargeLine[] | null> => {
    // A whole date only: the field reports every keystroke, and a half-typed one accrues no
    // interest at all, which would flash the charges down to their bare balances.
    if (!lines.length || !/^\d{4}-\d{2}-\d{2}$/.test(payoffDate || '')) return null
    if (payoffDate === chargesPricedFor.current) return null
    // Nothing of the server's to re-price: a payoff collecting only added fees, or an existing
    // payment being edited, whose lines are what was actually collected and are not ours to move.
    if (!Object.keys(seededChargeAmounts.current).length) return null
    let quoted: PayoffChargeLine[]
    repricingFor.current = payoffDate
    try {
      const res: any = await getPayoffPaymentCharges(payoffDate)
      if (!Array.isArray(res?.charges)) return null
      quoted = res.charges
    } catch {
      return null
    }
    // Overtaken while we waited: the newer request owns the lines.
    if (repricingFor.current !== payoffDate) return null
    const prices = new Map<number, number>()
    quoted.forEach((line) => {
      const id = Number(line?.id)
      if (id > 0) prices.set(id, num(line?.amount))
    })
    const next = lines.map((line) => {
      const id = Number(line?.id)
      if (!(id > 0) || !prices.has(id)) return line
      if (num(line?.amount) !== num(seededChargeAmounts.current[id])) return line
      return { ...line, amount: prices.get(id) as number }
    })
    // The SERVER's new figures, not `next`: an edited line keeps the servicer's own amount, and
    // remembering that as the quote would let the next date change overwrite it.
    rememberChargePrices(quoted, payoffDate)
    return next
  }

  // Payoff: prefill the full amount required to close out the loan —
  //   outstanding principal
  //   + per-diem interest from the Interest Paid Thru date to the payoff date
  //   + unpaid interest, unpaid charges, unpaid late charges
  //   + escrow shortage (a negative escrow balance)
  // and allocate it across the Applies To buckets so Total Credited = Amount Paid.
  //
  // `keepDates` is for a requote after the servicer changes the Payment Received date: the dates
  // they entered stay, and the per-diem is quoted to that date. Resetting them to today here
  // snapped a backdated payoff straight back to today and quoted its interest to today as well.
  const fillPayoff = async (base?: Record<string, InputType>, { keepDates = false } = {}) => {
    const temp = cloneDeep(base || inputs)
    const today = formatDate(new Date(), 'yyyy-MM-dd')
    if (paymentInfo.nextPaymentDate) temp.paymentDueDate.value = formatDate(paymentInfo.nextPaymentDate, 'yyyy-MM-dd')
    if (!keepDates || !temp.paymentReceivedDate.value) temp.paymentReceivedDate.value = today
    if (!keepDates || !temp.paymentDepositedDate.value) temp.paymentDepositedDate.value = today

    // The payoff quote itself — shared with computeInterest() so the prefill and every later
    // re-split of this payment quote the same interest to the same date.
    const { balance, interestOwed: interest } = payoffInterestOwed(temp)
    const principal = balance
    // The charges this payoff collects, seeded once from the fill data: the loan's open charges
    // plus the fees this lender quotes on every payoff. Seeded once and kept across a re-quote, so
    // moving the payoff date re-prices the interest without discarding lines the servicer has
    // edited. A backend that does not send the list at all falls back to the plain unpaid-charges
    // balance, which is what this screen collected before the lines existed.
    const seed = paymentInfo?.payoffCharges
    const seeded = Array.isArray(seed)
    let chargeLines = payoffCharges
    if (seeded && !prefilledPayoffCharges.current) {
      chargeLines = cloneDeep(seed)
      prefilledPayoffCharges.current = true
      // The fill data carries no date, so its figures stand at today's interest.
      rememberChargePrices(chargeLines, today)
      setPayoffCharges(chargeLines)
    }
    // ...then re-priced to the payoff date, because a charge carrying a rate is worth a day less of
    // interest for every day the payoff is backdated. Without this the lines kept today's interest
    // however far back the payoff was dated, and the payment collected more than the demand it was
    // settling quoted: longhorn 311581, paid off 10/13, read 56c over on three force-placed
    // insurance charges at 18%.
    const repriced = await repricedChargeLines(
      chargeLines,
      // The pinned date wins: it is the servicer saying these charges were quoted good through an
      // earlier day, and a re-quote on a new payment date must not undo that.
      chargeInterestThroughRef.current || (temp.paymentReceivedDate.value as string),
    )
    if (repriced) {
      chargeLines = repriced
      setPayoffCharges(repriced)
    }
    const unpaidCharges =
      seeded || chargeLines.length ? sumPayoffCharges(chargeLines) : num(paymentInfo.unpaidChargesBalance)
    // Late charges owed at payoff: the ones already booked on the loan or carried in from the prior
    // servicer, PLUS the ones accrued since the last paid installment, which are posted nowhere and
    // so sit in no balance. The demand quotes both (its "Late Fees Unpaid/Due from Previous
    // Payments" and "Late Fees Due from Paid-to-Date" lines); collecting only the first recorded a
    // payoff short by every late charge the delinquency had run up - longhorn 313000 came out
    // $252.55 under both the demand and the prior servicer's figure.
    const accrued = await accruedLateCharges(temp)
    // `payoffUnpaidLateChargesDue` and NOT the loan's Unpaid Late Charges balance: a loan that
    // itemises that balance in charge rows already has every one of them seeded as a line in the
    // charge editor above, where the payoff actually settles them, so adding the balance here
    // demanded the same money a second time. The server sends the part no row will settle - the
    // figure an imported loan carries with no rows behind it - which is 0 as soon as the loan
    // itemises. See payoffCashBalance in getLoanPaymentFillData.
    const unpaidLate = Number((num(paymentInfo.payoffUnpaidLateChargesDue) + accrued.total).toFixed(2))
    const escrowBal = num(paymentInfo.escrowBalance)
    const escrowShortage = escrowBal < 0 ? -escrowBal : 0
    const escrowCredit = escrowBal > 0 ? escrowBal : 0
    const suspenseCredit = Math.max(0, num(paymentInfo.suspenseBalance))
    // Reserve money already held against this loan funds the payoff too, which is how the Payoff
    // page quotes it: its Construction Reserve Credit line is this same balance. Prefilling it here
    // is what makes the demand and the payment that settles it agree. On a converted construction
    // loan this is the whole reserve, so without it the recorded payoff was short by that amount
    // and the money stayed on the ledger of a loan marked paid in full.
    const reserveCredit = Math.max(0, num(paymentInfo.constructionReserveBalance))
    // The trust balance funds it on the same terms, and on a converted loan it is the ONLY one that
    // does: that loan declares no construction reserve, so its whole holdback sits in a trust
    // sub-ledger bucket and `constructionReserveBalance` is null. The Payoff page credits this
    // balance on its demand (its Trust Balance line is this same figure), so without it here the
    // payment recorded against that demand collected the held money from the borrower in cash and
    // left the bucket funded on a loan marked paid in full - longhorn 314433, $16,900 of a
    // $162,150 payoff.
    // The escrow repair holdback, now its own source box drawing its own sub-ledger bucket. On a
    // converted loan this is the whole of what the servicer holds: that loan declares no
    // construction reserve and its trust-ledger table is empty, so without this the payment
    // recorded against the demand collected the held money from the borrower in cash and left the
    // bucket funded on a loan marked paid in full (longhorn 314433, $16,900 of a $162,150 payoff).
    const holdbackCredit = Math.max(0, num(paymentInfo.escrowRepairHoldbackBalance))
    // The loan's OWN trust ledger, a different table. Pooled into the box above until the two were
    // split; they are separate now because the draw has to leave the ledger actually holding it.
    const trustCredit = Math.max(0, num(paymentInfo.trustBalance))

    // Obligations (Applies To) — Total Credited sums to this.
    const obligations = Number((principal + unpaidCharges + interest + unpaidLate + escrowShortage).toFixed(2))
    // Escrow credit, suspense on hand, the construction reserve and the trust balance fund part of
    // it (applied as sources), so the borrower's cash is the remainder. Never negative: held money
    // larger than the payoff hands the surplus back through a disbursement, not by quoting negative
    // cash here.
    const cash = Math.max(
      0,
      Number(
        (obligations - escrowCredit - suspenseCredit - reserveCredit - holdbackCredit - trustCredit).toFixed(2),
      ),
    )
    // ...which means each held bucket can only ever be applied up to what is actually owed. Applied
    // in the order they are listed, so a payoff smaller than everything held draws the reserve down
    // first and leaves the trust bucket alone, rather than part-drawing both.
    const reserveApplied = Math.min(reserveCredit, Math.max(0, obligations - escrowCredit - suspenseCredit))
    const holdbackApplied = Math.min(
      holdbackCredit,
      Math.max(0, obligations - escrowCredit - suspenseCredit - reserveApplied),
    )
    const trustApplied = Math.min(
      trustCredit,
      Math.max(0, obligations - escrowCredit - suspenseCredit - reserveApplied - holdbackApplied),
    )

    temp.amountDue.value = obligations
    temp.amountPaid.value = cash
    temp.sourceImpound.value = escrowCredit || ''
    temp.sourceSuspense.value = suspenseCredit || ''
    if (temp.sourceConstructionReserve) temp.sourceConstructionReserve.value = reserveApplied || ''
    if (temp.sourceEscrowRepairHoldback) temp.sourceEscrowRepairHoldback.value = holdbackApplied || ''
    if (temp.sourceTrustBalance) temp.sourceTrustBalance.value = trustApplied || ''
    temp.interest.value = interest
    temp.unpaidInterest.value = 0
    if (temp.capitalizedInterest) temp.capitalizedInterest.value = 0
    // A payoff settles every unpaid balance the loan carries, this bucket included - its rows are
    // seeded into the payoff's charge editor like any other charge - so nothing is left owing.
    if (temp.unpaidDefaultInterest) temp.unpaidDefaultInterest.value = 0
    temp.principal.value = principal
    // Unpaid charges flow through Pay Charges; the read-only Charges field mirrors it.
    temp.payCharges.value = unpaidCharges > 0 ? { unpaid: unpaidCharges, accrued: 0 } : {}
    temp.charges.value = unpaidCharges
    temp.lateFee.value = unpaidLate
    // Where the figure came from, since it is two amounts added together and the servicer is the
    // one who decides whether to collect it. The same figure the box is filled with, not the loan's
    // whole Unpaid Late Charges balance: once the loan itemises that balance the charge editor
    // collects it instead, and naming it here would claim money this box is not asking for.
    const bookedLate = num(paymentInfo.payoffUnpaidLateChargesDue)
    temp.lateFee.tooltip = accrued.total
      ? `$${thousandSeperator(accrued.total.toFixed(2))} accrued over ${accrued.installments} late payment${
          accrued.installments === 1 ? '' : 's'
        } through ${temp.paymentReceivedDate?.value || ''}${
          bookedLate ? ` + $${thousandSeperator(bookedLate.toFixed(2))} unpaid from previous payments` : ''
        }`
      : ''
    temp.escrows.value = escrowShortage
    temp.escrowsTotal.value = escrowShortage
    // A fresh quote is paid to the cent, so there is no overage to hold. Keep only what the
    // servicer parked in Suspense by hand and forget this allocator's own last figure.
    if (temp.suspense) {
      const heldByHand = manualSuspense(temp)
      temp.suspense.value = heldByHand > 0 ? heldByHand : ''
    }
    autoSuspenseRef.current = 0
    // Amount Due above is the whole quote again, so nothing of the waiver is off it any more. The
    // waiver itself stands - it is the servicer's decision, not the quote's - and the next re-split
    // takes it off this new figure.
    waivedInterestOff.current = 0
    ;['mortgage', 'buydown', 'defaultInterest', 'servicingFee'].forEach((k) => {
      if (temp[k]) temp[k].value = ''
    })
    setInputs(temp)
  }

  // Defaults applied when the modal opens for a NEW payment: due date = servicing
  // Next Payment Date, received/deposited = today, amount due & paid = servicing
  // Total Payment, and the Applies To distribution as if Fill on Principal was clicked.
  const fillDefaults = async (base?: Record<string, InputType>) => {
    const temp = cloneDeep(base || inputs)
    const today = formatDate(new Date(), 'yyyy-MM-dd')
    if (paymentInfo.nextPaymentDate) temp.paymentDueDate.value = formatDate(paymentInfo.nextPaymentDate, 'yyyy-MM-dd')
    temp.paymentReceivedDate.value = today
    temp.paymentDepositedDate.value = today
    const totalPayment = Number(removeComma(String(paymentInfo.amountDue ?? ''))) || 0
    temp.amountDue.value = totalPayment
    temp.amountPaid.value = totalPayment
    await onFillEscrow(temp)
  }

  const calculateNonDutchAmountDue = (paymentDueDate: string) => {
    const { trustLedgerData: data, initialLoanAmount, rate, firstPaymentDate } = paymentInfo
    let amount = roundToCents((initialLoanAmount * rate) / 100 / 12)
    data
      .filter((v: any) => v.date < paymentDueDate)
      .map((item: any) => {
        const { inspectionFee, wireFee, titleUpdateFee, additionHoldback, payment, deposit, date } = item
        const nextPaymentDate = getNextPaymentDate(firstPaymentDate, date)
        const diffDays = 30 + Number(nextPaymentDate.split('-')[2]) - Number(date.split('-')[2])
        let drawDisbursed = 0
        ;[inspectionFee, wireFee, titleUpdateFee, additionHoldback].map((v) => {
          drawDisbursed += Number(v || 0)
        })
        drawDisbursed += Number(deposit || payment)
        amount += roundToCents((drawDisbursed * rate * +diffDays) / 100 / 360)
      })
    return roundToCents(amount)
  }

  const canShowHistory = !!editData?.id

  const showHistory = (key: string) => {
    if (!editData?.id) return
    const input = inputs[key]
    openAuditLog({
      table: 'LoanPayment',
      field: key,
      fieldLabel: input?.title || key,
      keys: {
        id: editData.id,
        field: key,
      },
      map: key === 'type' ? LoanPaymentType : input?.options,
      renderValue: key === 'type' || input?.options
        ? undefined
        : (value: any) => {
            if (value === null || value === undefined) return ''
            if (typeof value === 'object') return JSON.stringify(value)
            return String(value)
          },
    })
  }

  const renderBtnPayCharges = () => {
    // Late charge for the received date that the servicer has not (fully) entered under Late Fees.
    // Never auto-filled - whether to COLLECT it from this payment is their decision - but on a new
    // payment it is charged to the loan either way, which is what the notice states.
    const lateChargeNotEntered = lateChargeShortfall()
    // A Pay Charges selection confirmed for one received date, with the date since moved. Only
    // charges that actually accrue interest are affected, so a selection of flat fees says nothing.
    const chargesSelectionStale =
      !!payChargesPricedFor &&
      payChargesPricedFor !== (inputs.paymentReceivedDate?.value as string) &&
      payChargesTotal(inputs.payCharges?.value) > 0 &&
      num((inputs.payCharges?.value as any)?.accrued) > 0
    return (
      <>
        {lateChargeNotEntered > 0 && (
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            A ${thousandSeperator(lateChargeNotEntered.toFixed(2))} late charge applies for this received date and is
            not included in this payment. Saving it will charge the loan ${
              thousandSeperator(lateChargeNotEntered.toFixed(2))
            }{' '}
            as an unpaid late charge, which the borrower still owes; enter it under Late Fees to collect it with this
            payment instead.
          </p>
        )}
        {chargesSelectionStale && (
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            The charges on this payment were priced for {formatDate(payChargesPricedFor)}, not{' '}
            {formatDate(inputs.paymentReceivedDate?.value as string)}. Each charge carrying an interest rate is worth a
            little less for an earlier date, so reopen Pay Charges to re-read them; the amounts already entered are
            kept as they are.
          </p>
        )}
        <p>
          Not Deferred Charges:{' '}
          {/* Only what is still owed. A charge paid in full is worth $0, and listing it read
              "$0 + $0 + $50 + ...", so a settled fee looked collectable right beside the Pay
              Charges picker that now leaves it out. */}
          {(quotedLoanCharges || paymentInfo?.loanCharges || [])
            .filter((v: any) => num(v))
            .map((v: any) => `$${v}`)
            .join(' + ')}
        </p>
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-4">
            <Button link onClick={() => setShowLoanCharges(true)}>
              Pay Charges
            </Button>
            <Button link onClick={() => setShowDistributions(true)}>
              {distributions ? 'Distributions (edited)' : 'Preview / Edit Distributions'}
            </Button>
            {phonePaymentAvailable !== false && phonePaymentAvailable !== 'checking' && canTakePhonePayment && !editData.id && (
              <Button link onClick={onTakePhonePayment}>
                Take Payment by Phone
              </Button>
            )}
          </div>
          <Checkbox
            id="omitFromReport"
            title="Omit From 1098 Reporting"
            checked={inputs.omitFromReport?.value || false}
            onChange={(checked) => onChange('omitFromReport', checked)}
            className="w-auto"
            history={canShowHistory}
            showHistory={() => showHistory('omitFromReport')}
          />
        </div>
      </>
    )
  }

  const renderPayCharges = () => {
    const payCharges = inputs.payCharges.value
    if (!payCharges || Object.keys(payCharges).length === 0) return null
    // A payoff lists its charges in the Payoff Charges editor below, so repeating the lump sum here
    // would show the same money twice and read as two separate collections.
    if (inputs.type?.value === 'payoff') return null

    // One line per charge being paid, so a payment covering several of them says which. A payoff
    // fills the charges total without naming them (it clears every charge), so fall back to it.
    const { unpaid, accrued, items } = payCharges
    return (
      <div>
        {Array.isArray(items) && items.length ? (
          <div className="mt-5">
            {items.map((item: any) => (
              <p key={item.id}>
                {`#${item.id}: Unpaid Amount: $${thousandSeperator(item.unpaid)}`}
                {num(item.accrued) ? `, Accrued Amount: $${thousandSeperator(item.accrued)}` : ''}
              </p>
            ))}
          </div>
        ) : (
          <p className="mt-5">
            Unpaid Amount: ${thousandSeperator(unpaid)}
            {accrued ? `, Accrued Amount: $${thousandSeperator(accrued)}` : ''}
          </p>
        )}
      </div>
    )
  }

  // The loan's balances, read-only, above the payment being keyed. Same figures and same source as
  // Servicing Information > Balances, so the two screens cannot quote a borrower different numbers:
  // they all arrive on `paymentInfo` from the servicing record, and the Loan Payments screen
  // re-reads them (refreshServicingDates) after every apply / reverse / delete. The pipeline's
  // Apply Payment overlay fills `paymentInfo` the same way, so it shows this panel too.
  //
  // These are the balances BEFORE this payment, and deliberately do not move as the allocation is
  // typed: they are what a servicer reads in order to decide the allocation, so a figure that
  // shifted under them while they typed would be no use for that.
  const renderBalances = () => {
    // Nothing fetched yet (the overlay opens before its fill data lands). Six $0.00 tiles would
    // read as a loan that owes nothing rather than as a loan nobody has looked up yet.
    if (!paymentInfo || Object.keys(paymentInfo).length === 0) return null
    const impound = num(paymentInfo.escrowBalance)
    const upbSet = paymentInfo.currentUpb != null && paymentInfo.currentUpb !== ''
    const tiles: Array<{ label: string; value: string; hint?: string; negative?: boolean }> = [
      {
        label: 'Principal Balance',
        // Left blank when the servicing record carries no UPB, exactly as the Balances section
        // leaves it. Falling back to the loan amount would state a balance nobody computed.
        value: upbSet ? thousandSeperatorWithPrefix(Number(paymentInfo.currentUpb)) : '\u2014',
      },
      {
        label: 'Impound Balance',
        value: thousandSeperatorWithPrefix(impound),
        // A negative impound is a shortage the borrower owes the bucket - what the payoff fill
        // above collects as `escrowShortage`. Say so, rather than leave a bare minus sign that
        // reads at a glance like a credit.
        ...(impound < 0 ? { hint: 'shortage', negative: true } : {}),
      },
      { label: 'Suspense Balance', value: thousandSeperatorWithPrefix(num(paymentInfo.suspenseBalance)) },
      { label: 'Unpaid Interest', value: thousandSeperatorWithPrefix(num(paymentInfo.unpaidInterestBalance)) },
      { label: 'Unpaid Charges', value: thousandSeperatorWithPrefix(num(paymentInfo.unpaidChargesBalance)) },
      { label: 'Unpaid Late Charges', value: thousandSeperatorWithPrefix(num(paymentInfo.unpaidLateChargesBalance)) },
    ]
    // Reserve money the loan is still holding. Only for a loan that has the bucket: the server
    // sends null otherwise, rather than a $0.00 reserve that does not exist. The Interest Reserve
    // one matters most here, since the Source of Funds section above has a box that draws on it -
    // the servicer was being asked how much to draw without being told how much there is.
    if (paymentInfo.interestReserveBalance != null)
      tiles.push({
        label: 'Interest Reserve',
        value: thousandSeperatorWithPrefix(num(paymentInfo.interestReserveBalance)),
        ...(num(paymentInfo.interestReserveBalance) < 0 ? { hint: 'overdrawn', negative: true } : {}),
      })
    if (paymentInfo.constructionReserveBalance != null)
      tiles.push({
        label: 'Construction Reserve',
        value: thousandSeperatorWithPrefix(num(paymentInfo.constructionReserveBalance)),
        ...(num(paymentInfo.constructionReserveBalance) < 0 ? { hint: 'overdrawn', negative: true } : {}),
      })
    // The escrow repair holdback, on its own tile rather than pooled into the trust balance below.
    // On a converted loan this is where the construction money actually is, since the conversion
    // populated no structure reserve amounts, so the two tiles above stay off and this is the one
    // that answers "what is this loan holding" (longhorn 314433: $16,900). Same name the payoff
    // credit uses, which is the point - a servicer reconciling the demand against the payment
    // should be reading one vocabulary.
    //
    // Note the Source of Funds section still settles this through Trust Balance; there is no
    // separate source column for it. The tile tells the servicer how much of that source is the
    // holdback. See `trustCredit` above.
    if (paymentInfo.escrowRepairHoldbackBalance != null)
      tiles.push({
        label: 'Escrow Repair Holdback',
        value: thousandSeperatorWithPrefix(num(paymentInfo.escrowRepairHoldbackBalance)),
        ...(num(paymentInfo.escrowRepairHoldbackBalance) < 0 ? { hint: 'overdrawn', negative: true } : {}),
      })
    // What is left in the loan's own trust ledger once every named bucket above is out.
    if (paymentInfo.trustBalance != null)
      tiles.push({
        label: 'Trust Balance',
        value: thousandSeperatorWithPrefix(num(paymentInfo.trustBalance)),
        ...(num(paymentInfo.trustBalance) < 0 ? { hint: 'overdrawn', negative: true } : {}),
      })
    return (
      <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <span className="text-sm font-semibold text-shade-blue dark:text-gray-200">Loan Balances</span>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">Current, before this payment</span>
        </div>
        <div className="grid md:grid-cols-6 grid-cols-3 gap-x-3 gap-y-2">
          {tiles.map(({ label, value, hint, negative }) => (
            <div key={label}>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
              <div
                className={`text-sm font-semibold ${negative ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}
              >
                {value}
              </div>
              {hint && <div className="text-[11px] text-red-600">{hint}</div>}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const isOtherCash = OTHER_CASH_TYPES.includes(inputs.type?.value as string)
  // Read once per render rather than inside the field loop, so the note and the figure in it
  // cannot be computed from different states.
  const shortOfPayoff = payoffShortfall(inputs)

  /**
   * The suspense bucket read forward through this payment: what the loan already holds, what this
   * payoff puts in, what it draws back out as a source of funds, and what the borrower refund at
   * the bottom of the screen takes with it.
   *
   * Smartsheet row 548 (lbetty): the overage and the refund are keyed in two different places -
   * the Suspense field up here, the payee and the amount down in Other Payments - and nothing on
   * the screen added them up, so a mistyped refund was invisible until the ledger disagreed. Worse
   * than a wrong figure: a refund keyed over the overage makes the backend refuse to pay it out of
   * suspense at all (`withBorrowerRefundBucket` is all-or-nothing), so the payout lands on the
   * Construction Reserve and the overage stays in suspense for ever, which is row 439 again.
   *
   * Only on a payoff, the one payment type that holds an overage to hand back. On every other type
   * a line paid to the borrower is money collected and passed through, which is how
   * `otherPaymentsCollected` reads it too.
   */
  const isPayoffPayment = inputs.type?.value === 'payoff'
  const suspenseRoll = suspenseAfterRefund({
    opening: num(paymentInfo?.suspenseBalance),
    credited: num(inputs.suspense?.value),
    applied: num(inputs.sourceSuspense?.value),
    rows: otherPayments,
  })
  const showSuspenseRoll = isPayoffPayment && (suspenseRoll.credited > 0 || suspenseRoll.refunded > 0)
  const borrowerPayee = payees.find((payee) => String(payee.payeeType || '').trim() === 'Borrower')

  /**
   * Raises the refund line for the whole of the Suspense field, so the amount cannot be mistyped at
   * all - the other half of row 548's ask ("can we have a button that says to push to refund").
   *
   * The whole field rather than `autoSuspenseRef` (the overage this allocator put there) because
   * that is the figure the backend weighs the refund against, so it is the largest amount that is
   * certain to post against suspense. On a payoff the two are the same unless the servicer has
   * also parked money there by hand, which is why the label says "in suspense" and not "overage".
   */
  const onRefundOverage = () => {
    if (!borrowerPayee || !(suspenseRoll.credited > 0)) return
    onOtherPaymentsChange([
      ...otherPayments,
      {
        payeeType: borrowerPayee.payeeType,
        payeeRefId: borrowerPayee.payeeRefId ?? null,
        payeeName: borrowerPayee.name,
        payeeAddress: borrowerPayee.address || '',
        amount: suspenseRoll.credited.toFixed(2),
        memo: BORROWER_OVERAGE_MEMO,
      },
    ])
  }

  /**
   * Offered only while the payment carries no refund yet. Once one is keyed the button is gone: a
   * second press would queue the borrower a second check for the same overage, which is the
   * double-refund hazard the credit-balance suggestion already had on row 439.
   */
  const refundOverageAction =
    showSuspenseRoll && borrowerPayee && suspenseRoll.credited > 0 && suspenseRoll.refunded === 0 ? (
      <span className="cursor-pointer text-shade-blue hover:underline text-sm" onClick={onRefundOverage}>
        Refund the {thousandSeperatorWithPrefix(suspenseRoll.credited)} in suspense to {borrowerPayee.name}
      </span>
    ) : undefined

  const suspenseRollFooter = showSuspenseRoll ? <PayoffSuspenseSummary roll={suspenseRoll} /> : undefined

  return (
    <>
      <Modal title="Loan Payment" isOpen={true} onClose={onClose} onOk={onModalOk} loading={loading}>
        <>
        {renderBalances()}
        <div className="grid md:grid-cols-4 grid-cols-2 gap-2">
          {Object.keys(inputs).map((key, index: number) => {
            const input = inputs[key]
            // Payment Due Date only applies to a Regular Payment (scheduled); hide it
            // for Payoff / Other Cash.
            if (key === 'paymentDueDate' && inputs.type?.value !== 'payment') return null
            // Other Cash sits outside the payment schedule: there is no scheduled
            // Amount Due, and so nothing for the interest to fall short of.
            if (
              isOtherCash &&
              ['amountDue', 'unpaidInterest', 'capitalizedInterest', 'unpaidDefaultInterest'].includes(key)
            )
              return null
            // Only on a loan actually running a default rate, and on one that has already booked a
            // shortfall. A permanent $0 beside Default Interest on every other loan reads as a
            // figure that failed to compute.
            if (
              key === 'unpaidDefaultInterest' &&
              !defaultRateInfo.rate &&
              !num(inputs.unpaidDefaultInterest?.value)
            )
              return null
            // The two shortfall fields are alternatives, so only the one this loan uses is shown.
            // Both on screen, one of them permanently $0, reads as a figure that failed to compute.
            if (key === 'capitalizedInterest' && !capitalizesInterest) return null
            if (key === 'unpaidInterest' && capitalizesInterest) return null
            // Only a loan that holds a construction reserve can apply one. Same condition the
            // Loan Balances tile above uses, so the box appears exactly when there is a balance
            // beside it to draw on.
            if (key === 'sourceConstructionReserve' && paymentInfo?.constructionReserveBalance == null) return null
            // Likewise for the trust balance: only a loan holding one can apply it, and the server
            // sends null when there is no such bucket. On a converted loan this is the box that
            // appears and the Construction Reserve one that does not.
            if (key === 'sourceEscrowRepairHoldback' && paymentInfo?.escrowRepairHoldbackBalance == null) return null
            if (key === 'sourceTrustBalance' && paymentInfo?.trustBalance == null) return null
            if (['paymentDueDate', 'amountDue', 'amountPaid'].includes(key)) {
              let fillFunction: Function | null = null
              if (key == 'paymentDueDate') fillFunction = onFillDate
              if (key == 'amountDue') fillFunction = onFillDue
              // Fill copies the scheduled Amount Due, which Other Cash doesn't have.
              if (key == 'amountPaid') fillFunction = isOtherCash ? null : onFillPaid
              const fill = fillFunction
              input.additionalElements = fill ? (
                <span className="cursor-pointer text-shade-blue hover:underline" onClick={() => fill()}>
                  Fill
                </span>
              ) : undefined
            }
            if (key == 'interest') {
              // The breakdown explains the scheduled interest split, which is not
              // applicable to a manually allocated Other Cash entry.
              input.additionalElements = isOtherCash ? undefined : (
                <span
                  className="cursor-pointer text-shade-blue hover:underline text-xs"
                  onClick={() => setShowInterestCalc(true)}
                >
                  How?
                </span>
              )
            }
            // Interest the payment did not cover. Carried as an Unpaid Interest receivable by
            // default; "Waive" forgives it instead, which is the one thing the servicer could not
            // say here before - the amount is booked and waived on the spot, so the Charges screen
            // keeps the record that it was charged and then given up (same trail as waiving a fee).
            if (key == 'unpaidInterest') {
              const shortfall = Number(removeComma(input.value) || 0)
              input.title = waiveUnpaidInterest === null ? 'Unpaid Interest' : 'Interest Waived'
              input.additionalElements =
                shortfall > 0 || waiveUnpaidInterest !== null ? (
                  <span
                    className="cursor-pointer text-shade-blue hover:underline text-xs"
                    onClick={() => onToggleWaiveInterest(shortfall)}
                  >
                    {waiveUnpaidInterest === null ? 'Waive' : 'Carry it instead'}
                  </span>
                ) : undefined
            }
            if (key == 'lateFee') {
              // This installment's assessed charge, with what is already entered taken off. The
              // link collects the rest from this payment; leaving it is what charges it to the loan.
              const notEntered = lateChargeShortfall()
              input.additionalElements =
                notEntered > 0 ? (
                  <span
                    className="cursor-pointer text-shade-blue hover:underline text-xs"
                    onClick={onPayLateCharge}
                  >
                    Pay ${thousandSeperator(notEntered.toFixed(2))}
                  </span>
                ) : undefined
            }
            if (key == 'suspense') {
              // Only the action belongs here: additionalElements render inside the field's floating
              // label, which is absolutely positioned over the box, so the note explaining the
              // overage used to sit on top of the amount. It is rendered under the field instead.
              input.additionalElements = (
                <span className="cursor-pointer text-shade-blue hover:underline" onClick={onFillAllSuspense}>
                  Fill All
                </span>
              )
            }
            if (key == 'escrowsTotal') {
              if (inputs.mortgage.value?.toString().length || inputs.escrows.value?.toString().length) {
                input.disabled = true
                input.value = Number(removeComma(inputs.mortgage.value)) + Number(removeComma(inputs.escrows.value))
              } else {
                input.disabled = false
              }
            }
            // Charges is read-only and mirrors the total being paid via Pay Charges — the balances
            // and the interest accrued on them, across every charge picked.
            if (key == 'charges') {
              input.value = payChargesTotal(inputs.payCharges?.value)
            }
            if (key == 'totalCredited') {
              let total = 0
              // Suspense counts: it is where money paid over what the loan owes is held, so
              // leaving it out made Total Credited stop tying to Amount Paid the moment any
              // funds went there — including after Fill All Suspense, which used to drive this
              // figure to zero.
              ;[
                'principal',
                'interest',
                'escrowsTotal',
                'buydown',
                'lateFee',
                'servicingFee',
                'defaultInterest',
                'suspense',
              ].map((key) => {
                total += Number(removeComma(inputs[key]?.value) || 0)
              })
              total += payChargesTotal(inputs.payCharges?.value)
              // The borrower refund on a payoff is left out for the same reason the allocator
              // leaves it out: it is paid out of the Suspense already counted above, so adding it
              // here would credit the overage twice and read as over-allocated. See payoffRefund.
              total += otherPaymentsCollected(otherPayments, inputs.type?.value === 'payoff')
              input.value = Math.round(total * 100) / 100
            }
            if (key == 'btnPayCharges') (input as any).render = renderBtnPayCharges
            if (key == 'omitFromReport') return null
            if (key == 'payCharges') (input as any).render = renderPayCharges
            // How the money was tendered. The list is the company's own, so staff who may extend
            // the payment option lists get the same inline editor the Payment Code field uses.
            if (key == 'paymentMethod') {
              ;(input as any).options = getOptionValues(paymentMethodOptions)
              input.additionalElements = hasPermission('Loan.Servicing.Payment.Code.Add') ? (
                <span className="btn-link" onClick={() => setPaymentModal(SettingKey.PAYMENT_METHOD)}>
                  Add Method
                </span>
              ) : undefined
            }
            if (key == 'paymentCode') {
              if (!hasPermission('Loan.Servicing.Payment.Code.Add')) return null
              ;(input as any).options = getOptionValues(paymentCodeOptions)
              input.additionalElements = (
                <span className="btn-link" onClick={() => setPaymentModal(SettingKey.PAYMENT_CODE)}>
                  Add Code
                </span>
              )
            }
            return (
              <div className={`col-span-${input.span}`}>
                <RenderInput
                  input={canShowHistory ? input : { ...input, history: false }}
                  Key={key}
                  onChange={onChange}
                  showHistory={canShowHistory ? showHistory : undefined}
                />
                {/* Says why the money is sitting in suspense, so a servicer who expected Principal
                    to take it can see that the loan's balance is what stopped it, not an error. */}
                {key == 'suspense' && autoSuspenseRef.current > 0 && (
                  <p className="mt-1 px-1 text-xs leading-snug text-gray-500">
                    Includes {thousandSeperatorWithPrefix(autoSuspenseRef.current)} paid over the amount owed, held in
                    suspense
                  </p>
                )}
                {/* The other side of it: a payoff that comes up short has nothing to put in this box,
                    so say by how much here rather than leave it blank. Only the amount is red - the
                    sentence around it reads as an explanation, not an error. */}
                {key == 'suspense' && shortOfPayoff > 0 && (
                  <p className="mt-1 px-1 text-xs leading-snug text-gray-500">
                    <span className="font-semibold text-red-600">{thousandSeperatorWithPrefix(shortOfPayoff)}</span>{' '}
                    short of the amount owed, carried here so Principal and Interest still collect the
                    quoted payoff
                  </p>
                )}
                {key.startsWith('custom') && input.inputType === 'custom' && (
                  <div className="my-2" key={`custom-${index}`}>
                    {input.title && <p className="font-semibold text-sm mb-1">{input.title}</p>}
                    <div className="border-slate-300 border-b-[1px]"></div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {/* Only a payoff keys its charges line by line; every other type uses Pay Charges. */}
        {inputs.type?.value === 'payoff' && (
          <div className="mt-4">
            <PayoffChargesEditor
              value={payoffCharges}
              onChange={onPayoffChargesChange}
              categories={chargeCategories}
              interestThrough={chargeInterestThrough}
              interestThroughDefault={(inputs.paymentReceivedDate?.value as string) || ''}
              onInterestThroughChange={onChargeInterestThroughChange}
            />
          </div>
        )}
        <div className="mt-4">
          <OtherPaymentsEditor
            value={otherPayments}
            onChange={onOtherPaymentsChange}
            payees={payees}
            action={refundOverageAction}
            footer={suspenseRollFooter}
          />
        </div>
        </>
      </Modal>
      {showInterestCalc && (
        <Modal
          title="Interest Calculation"
          isOpen={true}
          onClose={() => setShowInterestCalc(false)}
          onOk={() => setShowInterestCalc(false)}
        >
          {(() => {
            const b = getInterestBreakdown()
            const basisMap: Record<string, string> = {
              '360': '30/360',
              '365': '30/365',
              actual_360: 'Actual/360',
              actual_365: 'Actual/365',
            }
            const basisLabel = basisMap[b.dailyRateBasis] || `${b.days}/${b.denom}`
            const accrualMap: Record<string, string> = {
              regular: 'Regular Period',
              due_to_due: 'Due Date to Due Date',
              received_to_received: 'Received Date to Received Date',
            }
            const accrualLabel = accrualMap[b.accrualPeriod] || ''
            const row = (label: string, value: string) => (
              <div className="flex justify-between gap-6 py-1">
                <span>{label}</span>
                <span className="font-medium whitespace-nowrap">{value}</span>
              </div>
            )
            return (
              <div className="p-4 text-sm">
                {b.isDutch ? (
                  <>
                    <p className="mb-2">Interest Type: Dutch — accrues on the current unpaid balance.</p>
                    {row('Unpaid Balance', `$${thousandSeperator(b.balance.toFixed(2))}`)}
                    {b.compoundedUnpaidInterest > 0 &&
                      row(
                        'Unpaid Balances Accruing Interest',
                        `+$${thousandSeperator(b.compoundedUnpaidInterest.toFixed(2))}`,
                      )}
                    {row('Current Interest Rate', `${b.rate}%`)}
                    {row('Daily Rate Basis', basisLabel)}
                    {accrualLabel && row('Accrual Period', accrualLabel)}
                    {row(`Interest Owed = Balance × Rate × ${b.days}/${b.denom}`, `$${thousandSeperator(b.interestOwed.toFixed(2))}`)}
                  </>
                ) : paymentInfo?.piBreakdown ? (
                  (() => {
                    const pb = paymentInfo.piBreakdown
                    return (
                      <>
                        <p className="mb-2">
                          Interest Type: Non-Dutch — daily-balance interest for the accrual period (
                          {formatDate(pb.monthStart)} – {formatDate(pb.monthEnd)}), so each draw only accrues from its
                          date.
                        </p>
                        {row('Opening Balance', `$${thousandSeperator(Number(pb.openingBalance).toFixed(2))}`)}
                        {Number(pb.compoundedUnpaidInterest) > 0 &&
                          row(
                            'Includes Unpaid Balances Accruing Interest',
                            `$${thousandSeperator(Number(pb.compoundedUnpaidInterest).toFixed(2))}`,
                          )}
                        {row('Interest Rate', `${pb.rate}%`)}
                        {row('Daily Rate Basis', `Actual/${pb.denom}`)}
                        {(pb.draws || []).map((d: any, i: number) => (
                          <div key={i}>
                            {row(`Draw ${formatDate(d.date)}`, `+$${thousandSeperator(Number(d.amount).toFixed(2))}`)}
                          </div>
                        ))}
                        {(pb.segments || []).map((s: any, i: number) => (
                          <div key={`seg-${i}`}>
                            {row(
                              s.fromDay === s.toDay ? `Day ${s.fromDay}` : `Days ${s.fromDay}–${s.toDay}`,
                              `$${thousandSeperator(Number(s.balance).toFixed(2))} for ${s.days} days`,
                            )}
                          </div>
                        ))}
                        <p className="mt-2 text-xs text-gray-500">
                          Interest = Σ(Balance × Days) × Rate ÷ (Days Basis × 100)
                        </p>
                        {row(
                          `(${thousandSeperator(Number(pb.sumDailyBalances).toFixed(2))} × ${pb.rate}) ÷ (${pb.denom} × 100)`,
                          `$${thousandSeperator(Number(pb.interestPortion ?? pb.amount).toFixed(2))}`,
                        )}
                        {Number(pb.principalPortion) > 0 &&
                          row(
                            'Principal (amortized to maturity)',
                            `+$${thousandSeperator(Number(pb.principalPortion).toFixed(2))}`,
                          )}
                        {row('Principal & Interest', `$${thousandSeperator(Number(pb.amount).toFixed(2))}`)}
                      </>
                    )
                  })()
                ) : (
                  <>
                    <p className="mb-2">Interest Type: Non-Dutch — interest is taken from the Amount Due.</p>
                    {row('Interest Owed (Amount Due)', `$${thousandSeperator(b.interestOwed.toFixed(2))}`)}
                  </>
                )}
                {b.interest < b.interestOwed && (
                  <div className="mt-3 pt-2 border-t border-gray-300">
                    <p className="mb-2">This payment doesn&apos;t fully cover the interest owed:</p>
                    {row('Funds applied (Paid + Suspense + Impound)', `$${thousandSeperator(b.amountApplied.toFixed(2))}`)}
                    {row('Less escrow / MI / fees / charges', `$${thousandSeperator(b.deductions.toFixed(2))}`)}
                    {row('Available for interest', `$${thousandSeperator(b.available.toFixed(2))}`)}
                    {row('Interest collected', `$${thousandSeperator(b.interest.toFixed(2))}`)}
                    {capitalizesInterest
                      ? row(
                          'Capitalized into Principal',
                          `$${thousandSeperator(b.unpaidInterest.toFixed(2))}`,
                        )
                      : row('Unpaid Interest', `$${thousandSeperator(b.unpaidInterest.toFixed(2))}`)}
                    {capitalizesInterest && (
                      <p className="mt-2 text-[11px] italic text-gray-500">
                        This loan is set to Principal (Negative Amortization), so the interest this payment did not
                        cover is added to the balance and accrues from the next period.
                      </p>
                    )}
                  </div>
                )}
                {b.defaultInterest > 0 && (
                  <div className="mt-3 pt-2 border-t border-gray-300">
                    <p className="mb-2">
                      Default Interest — charged at the default rate while the loan is past due (configured on the
                      Penalties screen).
                    </p>
                    {b.defaultRate ? row('Default Rate', `${b.defaultRate}%`) : null}
                    {b.defaultRate ? row('Current Rate', `${b.rate}%`) : null}
                    {b.defaultRate
                      ? row(
                          `Balance × (Default − Current) × ${b.defaultDays}/360`,
                          `$${thousandSeperator(b.defaultInterest.toFixed(2))}`,
                        )
                      : row('Default Interest', `$${thousandSeperator(b.defaultInterest.toFixed(2))}`)}
                  </div>
                )}
              </div>
            )
          })()}
        </Modal>
      )}
      {showDistributions && (
        <DistributionsModal
          paymentData={collectPaymentData()}
          // Only for a payment that is already saved: it tells the preview to split on the basis
          // that payment was distributed on. A new payment has no id and none is sent.
          paymentId={editData?.id || undefined}
          initialRows={distributions}
          onClose={(rows) => {
            // rows === null on cancel: keep whatever override (or none) was already set.
            if (rows) setDistributions(rows)
            setShowDistributions(false)
          }}
        />
      )}
      {showLoanCharges && (
        <LoanChargesModal
          // Priced to the date the payment says it was received, not to today: a charge carrying a
          // rate accrues daily, so a backdated payment must be offered the interest standing on
          // that day. See the notice in renderBtnPayCharges for what happens if the date moves
          // after a selection is confirmed.
          asOf={inputs.paymentReceivedDate?.value as string}
          onClose={(newData: IPayCharges | null) => {
            if (newData) {
              const newInputs = cloneDeep(inputs)
              newInputs['payCharges'].value = newData
              setInputs(newInputs)
              // The amounts just confirmed are the servicer's, priced to this date. Remembering it
              // lets the notice say so if the date later moves, rather than this quietly rewriting
              // figures they have agreed to.
              setPayChargesPricedFor((inputs.paymentReceivedDate?.value as string) || '')
            }
            setShowLoanCharges(false)
          }}
        />
      )}
      {showAdjustModal && pendingData && (
        <LoanPaymentAdjustModal
          appliedFunds={appliedFunds(inputs)}
          payCharges={payChargesTotal(inputs.payCharges?.value)}
          values={fillObject(adjustFieldKeys, (key: string) => Number(removeComma(inputs[key].value) || 0)) as any}
          onClose={() => {
            setShowAdjustModal(false)
            setPendingData(null)
          }}
          onConfirm={(
            adjustedValues: Record<string, number>,
            // Matches what the dialog declares it passes. The local annotation was narrower and
            // dropped `reason`, which a waiver carries.
            differedValues: Record<string, { amount: number; deferOption: DeferOption; reason?: string }>,
          ) => {
            const updatedData = {
              ...pendingData,
              ...adjustedValues,
            }
            setShowAdjustModal(false)
            setPendingData(null)
            // Late fees are no longer carried from here. The server books whatever this
            // installment's assessed charge comes to over and above the fee the payment actually
            // applies, which is both the never-entered part and anything this dialog held back -
            // the adjusted lateFee below is what gets saved, so the difference covers both.
            // Booking either here as well would charge the held-back amount twice.
            const { lateFee: lateFeeDeferral, ...deferred } = differedValues
            // Forgiving it is still this dialog's decision and carries a reason, so it rides on
            // the payment and the server books the charge and waives it in one go.
            const lateChargeWaiveReason =
              lateFeeDeferral?.deferOption === 'waive' ? lateFeeDeferral.reason || '' : undefined
            if (lateChargeWaiveReason !== undefined) updatedData.lateChargeWaiveReason = lateChargeWaiveReason
            onOk(editData.id, updatedData, deferred)
          }}
        />
      )}
      {phonePaymentData && (
        <PhonePaymentModal
          amount={Number(removeComma(phonePaymentData.amountPaid) || 0)}
          payment={phonePaymentData}
          defaultPayor={phonePaymentData.payor || ''}
          onClose={() => setPhonePaymentData(null)}
          onDone={(result) => {
            setPhonePaymentData(null)
            onPhonePaymentApplied?.(result)
          }}
        />
      )}
      {paymentModal && (
        <OptionModal
          title={paymentModal === SettingKey.PAYMENT_METHOD ? 'Update Payment Method options' : 'Update Payment Code options'}
          settingKey={paymentModal}
          onClose={(values: IOptionType[]) => {
            values &&
              dispatch(
                setGlobalConfig(
                  paymentModal === SettingKey.PAYMENT_METHOD
                    ? GlobalConfigType.PAYMENT_METHOD
                    : GlobalConfigType.PAYMENT_CODE,
                  values,
                ),
              )
            setPaymentModal(null)
          }}
          childLevel={1}
        />
      )}
    </>
  )
}
